import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { EtiquetaConfig } from '../../../services/clientes';
import { criarConferencia, registrarLeitura, transferirTodasLeiturasEntrePaletes, buscarDadosPaleteDescarregamento, analisarDivergenciasVolumes, removerLeiturasPedidosDivergentes, removerLeitura, listarPaletesPreCarregamento, buscarTotaisLogisticos } from '../../../services/conferencia';
import type { PaletePreCarregamento } from '../../../services/conferencia';
import { buscarConfiguracoesConferencia } from '../../../services/configuracoesConferencia';
import type { ConfiguracaoConferencia } from '../../../services/configuracoesConferencia';
import { gerarNovoPaleteConferente, buscarClientesConferente } from '../../../services/conferente';
import type { ClienteConferente, AreaDestino } from '../../../services/conferente';
import { gerarNovoPalete as gerarNovoPaleteRegular } from '../../../services/montagemPalete';
import type { AnaliseVolumePedido } from '../../../services/conferencia';
import type { LeituraResponse } from '../../../types/conferencia';
import CameraScannerModal from '../../../components/Common/CameraScannerModal';
import { salvarLeituraPendente, salvarConferenciaPendente, gerarTempConferenciaId } from '../../../services/offlineQueue';
import { buscarDadosPaleteDescarregamentoOffline } from '../../../services/offlineData';
import { utils } from '../../../utils';
import { criarDivergenciaOcorrenciaBatch } from '../../../services/divergenciaOcorrencias';
import type { DivergenciaOcorrenciaCreateData } from '../../../services/divergenciaOcorrencias';
import { listarTiposOcorrencia } from '../../../services/tiposOcorrenciaDivergencia';
import type { TipoOcorrenciaDivergencia } from '../../../services/tiposOcorrenciaDivergencia';
import { playScanSound, playDuplicateSound } from '../../../utils/soundAlerts';
import './PreCarregamento.css';

type Etapa = 'palete' | 'scanning' | 'divergencias';

type EtiquetaStatus = 'sucesso' | 'duplicado' | 'divergencia' | 'erro_parse' | 'erro' | 'removida';

interface HistoricoItem {
  id: number;
  codigo: string;
  status: EtiquetaStatus;
  mensagem?: string;
  timestamp: Date;
  leituraId?: number;
  conferenciaIdOrigem?: number;
}

interface DadosEtiqueta {
  campo: string;
  valor: string;
  /** Posição inicial do valor capturado dentro do código da etiqueta */
  posInicio?: number;
  /** Posição final (exclusiva) do valor capturado dentro do código da etiqueta */
  posFim?: number;
}

interface PaleteInfo {
  id: number;
  numero_palete: number;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  operacao_id: number;
  numero_operacao: number;
  doca: string | null;
  area_entrega_id: number;
  tipo_palete?: string;
  data_agendamento?: string;
}

interface ClienteInfo {
  id: number;
  razao_social: string;
  cnpj: string;
}

interface PreCarregamentoProps {
  backUrl?: string;
}

// Tipos de ocorrência padrão (fallback se a API não retornar nenhum)
const OCORRENCIA_TIPOS_FALLBACK = [
  { value: 'volume_faltante', label: 'Volume faltante' },
  { value: 'outro', label: 'Outro' },
];

interface DivergenciaFormData {
  pedidoKey: string;
  ocorrencia_tipo: string;
  justificativa: string;
  selected: boolean;
  selectedEtiquetas: Set<number>; // índices das etiquetas selecionadas (leituras)
  selectedFaltantes: Set<number>; // volumes faltantes selecionados
}

const STATUS_CONFIG: Record<EtiquetaStatus, { icon: string; color: string; bg: string; label: string }> = {
  sucesso: { icon: 'fas fa-check-circle', color: '#27ae60', bg: '#e8f5e9', label: 'Sucesso' },
  duplicado: { icon: 'fas fa-clone', color: '#f39c12', bg: '#fff3e0', label: 'Duplicado' },
  divergencia: { icon: 'fas fa-exclamation-triangle', color: '#e74c3c', bg: '#ffebee', label: 'Divergência' },
  erro_parse: { icon: 'fas fa-times-circle', color: '#9b59b6', bg: '#f3e5f5', label: 'Não Reconhecida' },
  erro: { icon: 'fas fa-times-circle', color: '#e74c3c', bg: '#ffebee', label: 'Erro' },
  removida: { icon: 'fas fa-trash-alt', color: '#6b7280', bg: '#f3f4f6', label: 'Removida' },
};

const PreCarregamentoConferencia: React.FC<PreCarregamentoProps> = ({ backUrl = '/conferencia' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isPWA = backUrl === '/conferente';

  const [etapa, setEtapa] = useState<Etapa>('palete');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Palete
  const [numeroPalete, setNumeroPalete] = useState('');
  const [paleteInfo, setPaleteInfo] = useState<PaleteInfo | null>(null);
  const paleteRef = useRef<HTMLInputElement>(null);

  // Cliente (auto-detected from palete)
  const [cliente, setCliente] = useState<ClienteInfo | null>(null);

  // Etiqueta configs (from palete lookup)
  const [etiquetaModelos, setEtiquetaModelos] = useState<any[]>([]);
  const [etiquetaConfigs, setEtiquetaConfigs] = useState<any[]>([]);

  // Scanning (input não-controlado para performance máxima em bipagem)
  const etiquetaRef = useRef<HTMLInputElement>(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [historicoVisivel, setHistoricoVisivel] = useState(false);
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [showScanOptions, setShowScanOptions] = useState(false);
  const scanOptionsRef = useRef<HTMLDivElement>(null);
  const [ultimoStatus, setUltimoStatus] = useState<{ status: EtiquetaStatus; mensagem: string } | null>(null);

  // Conferencia
  const [conferenciaId, setConferenciaId] = useState<number | null>(null);

  // Historico
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const nextId = useRef(0);

  // Divergências
  const [analiseVolumes, setAnaliseVolumes] = useState<AnaliseVolumePedido[]>([]);
  const [divergenciaForms, setDivergenciaForms] = useState<Record<string, DivergenciaFormData>>({});
  const [divergenciaLoading, setDivergenciaLoading] = useState(false);
  const [divergenciaSaving, setDivergenciaSaving] = useState(false);
  const [divergenciaSuccess, setDivergenciaSuccess] = useState(false);
  const [pedidosDivergentesRemovidos, setPedidosDivergentesRemovidos] = useState<string[]>([]);
  const [totalLeiturasRemovidas, setTotalLeiturasRemovidas] = useState(0);
  const [modoOcorrenciaUnica, setModoOcorrenciaUnica] = useState(false);
  const [ocorrenciaUnicaTipo, setOcorrenciaUnicaTipo] = useState('volume_faltante');
  const [ocorrenciaUnicaJustificativa, setOcorrenciaUnicaJustificativa] = useState('');
  const [expandedPedido, setExpandedPedido] = useState<string | null>(null);

  // Totais logísticos (peso, cubagem, volumes)
  const [totaisLogisticos, setTotaisLogisticos] = useState({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });

  // Rastreamento de volumes por pedido em tempo real (para detecção dinâmica de divergências)
  // Chave: numero_pedido, Valor: { total_volumes, volumes_bipados: Set<number>, volume_excedente: boolean }
  const [pedidosVolumes, setPedidosVolumes] = useState<Record<string, { total_volumes: number; volumes_bipados: Set<number>; volume_excedente: boolean; codigos_bipados: Map<number, string> }>>({});

  // Modal de duplicata
  const [duplicataModal, setDuplicataModal] = useState<{
    show: boolean;
    codigo: string;
    volumeAtual?: number | null;
    totalVolumes?: number | null;
    numeroPedido?: string | null;
    leituraId?: number;
  }>({ show: false, codigo: '' });
  const [duplicataLoading, setDuplicataLoading] = useState(false);
  const [duplicataOcorrenciaManual, setDuplicataOcorrenciaManual] = useState<string>('');
  const [duplicataObservacao, setDuplicataObservacao] = useState<string>('');

  // Modal de transferência entre paletes
  const [transferenciaModal, setTransferenciaModal] = useState<{
    show: boolean;
    codigo: string;
    numeroPaleteAnterior: number | null;
    conferenciaIdOrigem: number;
    leituraIdOrigem: number;
    dadosDecodificados: Record<string, unknown>;
  }>({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
  const [transferenciaLoading, setTransferenciaLoading] = useState(false);

  // Modal de confirmação de remoção de etiqueta
  const [remocaoModal, setRemocaoModal] = useState<{
    show: boolean;
    historicoItemId: number;
    codigo: string;
    leituraId: number;
    conferenciaIdOrigem: number;
  }>({ show: false, historicoItemId: 0, codigo: '', leituraId: 0, conferenciaIdOrigem: 0 });
  const [remocaoLoading, setRemocaoLoading] = useState(false);

  // Modal de confirmação de remoção de todas as etiquetas de um pedido
  const [remocaoPedidoModal, setRemocaoPedidoModal] = useState<{
    show: boolean;
    numeroPedido: string;
    totalVolumes: number;
    volumesBipados: number;
  }>({ show: false, numeroPedido: '', totalVolumes: 0, volumesBipados: 0 });
  const [remocaoPedidoLoading, setRemocaoPedidoLoading] = useState(false);

  // Gerar novo palete
  const [gerarPaleteLoading, setGerarPaleteLoading] = useState(false);
  const [gerarPaleteClienteModal, setGerarPaleteClienteModal] = useState<{
    show: boolean;
    clientes: ClienteConferente[];
  }>({ show: false, clientes: [] });

  // Seleção de área de destino para novo palete
  const [gerarPaleteAreaModal, setGerarPaleteAreaModal] = useState<{
    show: boolean;
    areas: AreaDestino[];
    clienteId: number;
  }>({ show: false, areas: [], clienteId: 0 });

  // Seleção de tipo de palete (normal/agendamento)
  const [gerarPaleteTipoModal, setGerarPaleteTipoModal] = useState<{
    show: boolean;
    clienteId: number;
    areaEntregaId: number;
  }>({ show: false, clienteId: 0, areaEntregaId: 0 });
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [showAgendamentoInput, setShowAgendamentoInput] = useState(false);

  // Configuração global (modo pré-carregamento)
  const [configGlobal, setConfigGlobal] = useState<ConfiguracaoConferencia | null>(null);

  // Lista de paletes (modo lista)
  const [paletesLista, setPaletesLista] = useState<PaletePreCarregamento[]>([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [clientesConferente, setClientesConferente] = useState<ClienteConferente[]>([]);
  const [clienteConferenteSelecionado, setClienteConferenteSelecionado] = useState<number | null>(null);
  const [semVinculosConferente, setSemVinculosConferente] = useState(false);
  const [loadingClientesConferente, setLoadingClientesConferente] = useState(false);

  // Busca por código de área de entrega (bipagem)
  const areaCodeRef = useRef<HTMLInputElement>(null);
  const [areaCodeInput, setAreaCodeInput] = useState('');
  const [areaCodeError, setAreaCodeError] = useState('');
  const [showAreaScanOptions, setShowAreaScanOptions] = useState(false);
  const [showAreaKeyboard, setShowAreaKeyboard] = useState(false);
  const [showAreaCameraScanner, setShowAreaCameraScanner] = useState(false);
  const areaScanOptionsRef = useRef<HTMLDivElement>(null);
  const [selectPaleteAreaModal, setSelectPaleteAreaModal] = useState<{
    show: boolean;
    paletes: PaletePreCarregamento[];
    areaCodigo: string;
  }>({ show: false, paletes: [], areaCodigo: '' });
  const [selectPaleteAreaError, setSelectPaleteAreaError] = useState('');
  const paleteModalRef = useRef<HTMLInputElement>(null);
  const [showPaleteModalScanOptions, setShowPaleteModalScanOptions] = useState(false);
  const [showPaleteModalKeyboard, setShowPaleteModalKeyboard] = useState(false);
  const [showPaleteModalCameraScanner, setShowPaleteModalCameraScanner] = useState(false);
  const paleteModalScanOptionsRef = useRef<HTMLDivElement>(null);

  // Tipos de ocorrência completos (incluindo automáticos) para o modal de duplicata
  const [todosOcorrenciaTipos, setTodosOcorrenciaTipos] = useState<TipoOcorrenciaDivergencia[]>([]);
  const temAutomaticoDuplicata = useMemo(() => {
    const tiposAuto = todosOcorrenciaTipos.filter(t => t.automatico && t.tipo_automatico);
    return tiposAuto.some(t => t.tipo_automatico === 'erro_conferente') && tiposAuto.some(t => t.tipo_automatico === 'duplicata_cliente');
  }, [todosOcorrenciaTipos]);

  // Tipos de ocorrência dinâmicos
  const [tiposOcorrencia, setTiposOcorrencia] = useState<{ value: string; label: string; ocorrencia_de_corte?: boolean }[]>(OCORRENCIA_TIPOS_FALLBACK);

  // Carregar tipos de ocorrência do backend
  useEffect(() => {
    const carregarTipos = async () => {
      try {
        // Carregar tipos não-automáticos para divergências gerais
        const result = await listarTiposOcorrencia(true, true);
        if (result.success && result.tipos.length > 0) {
          setTiposOcorrencia(result.tipos.map((t: TipoOcorrenciaDivergencia) => ({
            value: t.codigo,
            label: t.nome,
            ocorrencia_de_corte: t.ocorrencia_de_corte || false,
          })));
        }
        // Carregar todos os tipos ativos (incluindo automáticos) para o modal de duplicata
        const resultTodos = await listarTiposOcorrencia(true, false);
        if (resultTodos.success) {
          setTodosOcorrenciaTipos(resultTodos.tipos);
        }
      } catch {
        // Mantém fallback em caso de erro
      }
    };
    carregarTipos();
  }, []);

  // Fechar menu de opções ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (scanOptionsRef.current && !scanOptionsRef.current.contains(e.target as Node)) {
        setShowScanOptions(false);
      }
      if (areaScanOptionsRef.current && !areaScanOptionsRef.current.contains(e.target as Node)) {
        setShowAreaScanOptions(false);
      }
      if (paleteModalScanOptionsRef.current && !paleteModalScanOptionsRef.current.contains(e.target as Node)) {
        setShowPaleteModalScanOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Carregar configuração global de conferência
  useEffect(() => {
    const carregarConfig = async () => {
      try {
        const result = await buscarConfiguracoesConferencia();
        if (result.success && result.configuracao) {
          setConfigGlobal(result.configuracao);
        }
      } catch {
        // Usar padrão (palete_individual)
      }
    };
    carregarConfig();
  }, []);

  // Modo de pré-carregamento
  const getModoPreCarregamento = (): 'palete_individual' | 'lista_paletes' => {
    return configGlobal?.modo_pre_carregamento || 'palete_individual';
  };

  // Carregar lista de paletes quando o modo é lista e a etapa é palete
  useEffect(() => {
    if (etapa !== 'palete' || getModoPreCarregamento() !== 'lista_paletes') return;

    const carregarClientesEPaletes = async () => {
      setLoadingClientesConferente(true);
      try {
        const result = await buscarClientesConferente();
        if (result.success) {
          if (result.sem_vinculos || result.clientes.length === 0) {
            setSemVinculosConferente(true);
            setClientesConferente([]);
            setPaletesLista([]);
            return;
          }

          setSemVinculosConferente(false);
          setClientesConferente(result.clientes);

          if (result.auto_select && result.clientes.length === 1) {
            const clienteUnico = result.clientes[0];
            setClienteConferenteSelecionado(clienteUnico.id);
            carregarListaPaletesPreCarregamento(clienteUnico.id);
          } else {
            setPaletesLista([]);
          }
        }
      } catch {
        carregarListaPaletesPreCarregamento(null);
      } finally {
        setLoadingClientesConferente(false);
      }
    };
    carregarClientesEPaletes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa, configGlobal]);

  // Carregar lista de paletes para pré-carregamento
  const carregarListaPaletesPreCarregamento = async (clienteId: number | null) => {
    setLoadingLista(true);
    setError('');
    setAreaCodeInput('');
    setAreaCodeError('');
    try {
      const result = await listarPaletesPreCarregamento(clienteId);
      if (result.success) {
        setPaletesLista(result.paletes);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar lista de paletes');
    } finally {
      setLoadingLista(false);
    }
  };

  // Selecionar palete da lista e iniciar busca
  const handleSelecionarPaleteLista = (palete: PaletePreCarregamento) => {
    setNumeroPalete(String(palete.numero_palete));
    buscarPalete(palete.numero_palete);
  };

  // Buscar palete por código de área de entrega (bipagem da etiqueta do palete)
  const handleBuscarPorAreaCode = (codigo: string) => {
    const codigoTrimmed = codigo.trim();
    if (!codigoTrimmed) return;

    setAreaCodeError('');
    setAreaCodeInput('');

    // Filtrar paletes que correspondem ao código da área de entrega (case-insensitive)
    const codigoUpper = codigoTrimmed.toUpperCase();
    const paletesArea = paletesLista.filter(p =>
      (p.area_codigo && p.area_codigo.toUpperCase() === codigoUpper) ||
      (p.area_regiao && p.area_regiao.toUpperCase() === codigoUpper) ||
      (p.area_nome && p.area_nome.toUpperCase() === codigoUpper)
    );

    if (paletesArea.length === 0) {
      setAreaCodeError(`Nenhum palete encontrado para a área "${codigoTrimmed}"`);
      // Re-focar o input
      setTimeout(() => areaCodeRef.current?.focus(), 100);
      return;
    }

    if (paletesArea.length === 1) {
      // Somente um palete para essa área: selecionar automaticamente
      handleSelecionarPaleteLista(paletesArea[0]);
    } else {
      // Múltiplos paletes para a mesma área: solicitar seleção do palete
      setSelectPaleteAreaError('');
      setSelectPaleteAreaModal({ show: true, paletes: paletesArea, areaCodigo: codigoTrimmed });
    }
  };

  // Focus management
  useEffect(() => {
    if (etapa === 'palete' && getModoPreCarregamento() === 'palete_individual' && paleteRef.current) {
      paleteRef.current.focus();
    }
    if (etapa === 'scanning' && etiquetaRef.current) {
      etiquetaRef.current.focus();
    }
  }, [etapa]);

  // Auto-focar no input de área quando a lista de paletes carrega
  useEffect(() => {
    if (etapa === 'palete' && getModoPreCarregamento() === 'lista_paletes' && paletesLista.length > 0 && areaCodeRef.current) {
      setTimeout(() => areaCodeRef.current?.focus(), 200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletesLista, etapa]);

  // Manter foco no input de bipagem durante etapa scanning
  // Re-foca automaticamente quando o input perde o foco (ex: click fora)
  useEffect(() => {
    if (etapa !== 'scanning') return;
    const handleFocusBack = () => {
      setTimeout(() => {
        if (etapa === 'scanning' && etiquetaRef.current && document.activeElement !== etiquetaRef.current) {
          etiquetaRef.current.focus();
        }
      }, 100);
    };
    document.addEventListener('click', handleFocusBack);
    return () => document.removeEventListener('click', handleFocusBack);
  }, [etapa]);

  // Auto-clear last status after 4 seconds
  useEffect(() => {
    if (!ultimoStatus) return;
    const timer = setTimeout(() => setUltimoStatus(null), 4000);
    return () => clearTimeout(timer);
  }, [ultimoStatus]);

  // Play sound alert when scan status changes
  useEffect(() => {
    if (ultimoStatus?.status) {
      playScanSound(ultimoStatus.status);
    }
  }, [ultimoStatus]);

  // Summary counters
  const contadores = {
    total: historico.filter(h => h.status !== 'removida').length,
    sucesso: historico.filter(h => h.status === 'sucesso').length,
    duplicado: historico.filter(h => h.status === 'duplicado').length,
    divergencia: historico.filter(h => h.status === 'divergencia').length,
    erro: historico.filter(h => h.status === 'erro_parse' || h.status === 'erro').length,
    removida: historico.filter(h => h.status === 'removida').length,
  };

  // Verificação dinâmica de divergências em tempo real
  const temDivergenciaReal = useMemo(() => {
    for (const pedido of Object.values(pedidosVolumes)) {
      // Volume excedente (volume_atual > total_volumes)
      if (pedido.volume_excedente) return true;
      // Pedido incompleto (nem todos os volumes foram bipados)
      if (pedido.volumes_bipados.size < pedido.total_volumes) return true;
    }
    return false;
  }, [pedidosVolumes]);

  const addHistorico = (item: Omit<HistoricoItem, 'id'>) => {
    setHistorico(prev => [{ ...item, id: nextId.current++ }, ...prev]);
  };

  const formatCnpj = (value: string): string => {
    const nums = value.replace(/\D/g, '');
    if (nums.length <= 2) return nums;
    if (nums.length <= 5) return `${nums.slice(0, 2)}.${nums.slice(2)}`;
    if (nums.length <= 8) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5)}`;
    if (nums.length <= 12) return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8)}`;
    return `${nums.slice(0, 2)}.${nums.slice(2, 5)}.${nums.slice(5, 8)}/${nums.slice(8, 12)}-${nums.slice(12)}`;
  };

  // Gerar código de etiqueta irmã substituindo o volume_atual no código original
  // Utiliza o parseEtiqueta (que usa os regex do cadastro do cliente) para identificar
  // a posição exata do campo volume_atual e substituir pelo volume desejado
  const gerarCodigoIrmao = (codigoOriginal: string, volumeDesejado: number): string | null => {
    // Parsear o código usando as configurações do cliente
    const parsed = parseEtiqueta(codigoOriginal);
    if (!parsed.success) return null;

    // Identificar qual campo é o volume_atual pelo modelo do cliente
    const modelos = etiquetaModelos || [];
    const modeloComVolume = modelos.find((m: any) => m.ativo && m.campo_volume_atual);
    if (!modeloComVolume) return null;

    // Encontrar o dado do volume_atual no resultado do parse
    const volumeDado = parsed.dados.find(d => d.campo === modeloComVolume.campo_volume_atual);
    if (!volumeDado || volumeDado.posInicio === undefined || volumeDado.posFim === undefined) return null;

    // Determinar a largura correta para o volume usando total_volumes como referência.
    // O regex pode capturar apenas os dígitos significativos sem zeros à esquerda
    // (ex: regex "0*(\d+)" captura "1" de "0001"), mas o campo total_volumes preserva
    // o formato correto (ex: "0006" = 4 dígitos). Usamos essa largura como referência.
    let larguraVolume = volumeDado.posFim - volumeDado.posInicio;
    let posInicioAjustado = volumeDado.posInicio;
    if (modeloComVolume.campo_total_volumes) {
      const totalVolumesDado = parsed.dados.find(d => d.campo === modeloComVolume.campo_total_volumes);
      if (totalVolumesDado && totalVolumesDado.posInicio !== undefined && totalVolumesDado.posFim !== undefined) {
        const larguraTotal = totalVolumesDado.posFim - totalVolumesDado.posInicio;
        if (larguraTotal > larguraVolume) {
          // Estender a posição de substituição para a esquerda para incluir os zeros à esquerda
          // que o regex ignorou (ex: se capturou "1" em posição [14,15] de "0001",
          // ajustar para [11,15] para cobrir os "000" antes)
          const diferenca = larguraTotal - larguraVolume;
          const prefixo = codigoOriginal.substring(volumeDado.posInicio - diferenca, volumeDado.posInicio);
          if (/^0+$/.test(prefixo)) {
            posInicioAjustado = volumeDado.posInicio - diferenca;
            larguraVolume = larguraTotal;
          }
        }
      }
    }
    const volumeFormatado = String(volumeDesejado).padStart(larguraVolume, '0');
    return codigoOriginal.substring(0, posInicioAjustado)
      + volumeFormatado
      + codigoOriginal.substring(volumeDado.posFim);
  };

  // Parse etiqueta using client configs
  const parseEtiqueta = (codigo: string): { success: boolean; dados: DadosEtiqueta[]; erro?: string } => {
    const modelos = etiquetaModelos || [];
    let dados: DadosEtiqueta[] = [];

    if (modelos.length > 0) {
      for (const modelo of modelos) {
        if (!modelo.ativo) continue;
        const configs = (modelo.configs || []).filter((c: EtiquetaConfig) => c.ativo);
        if (configs.length === 0) continue;

        const dadosTentativa: DadosEtiqueta[] = [];
        let obrigatorioFaltando = false;

        for (const config of configs) {
          try {
            if (config.regex_pattern.startsWith('VALOR_PADRAO=')) {
              dadosTentativa.push({ campo: config.nome_campo, valor: config.regex_pattern.substring(13) });
              continue;
            }
            const regex = new RegExp(config.regex_pattern, 'd');
            const match = regex.exec(codigo);
            if (match) {
              const valor = match[1] || match[0];
              // Calcular posição do valor capturado dentro do código original
              let posInicio: number | undefined;
              let posFim: number | undefined;
              if (match[1] !== undefined && (match as any).indices?.[1]) {
                // Usar indices do regex (flag 'd') para posição exata do grupo de captura
                [posInicio, posFim] = (match as any).indices[1];
              } else if (match[1] !== undefined) {
                // Fallback sem indices
                posInicio = match.index;
                posFim = match.index + match[0].length;
              } else {
                posInicio = match.index;
                posFim = match.index + match[0].length;
              }
              dadosTentativa.push({ campo: config.nome_campo, valor, posInicio, posFim });
            } else if (config.obrigatorio) {
              obrigatorioFaltando = true;
              break;
            }
          } catch {
            // Regex invalido, ignorar
          }
        }

        if (!obrigatorioFaltando && dadosTentativa.length > 0) {
          dados = dadosTentativa;
          break;
        }
      }
    } else {
      // Fallback: usar etiqueta_configs flat (legado)
      const configs = (etiquetaConfigs || []).filter((c: EtiquetaConfig) => c.ativo);
      for (const config of configs) {
        try {
          if (config.regex_pattern.startsWith('VALOR_PADRAO=')) {
            dados.push({ campo: config.nome_campo, valor: config.regex_pattern.substring(13) });
            continue;
          }
          const regex = new RegExp(config.regex_pattern, 'd');
          const match = regex.exec(codigo);
          if (match) {
            const valor = match[1] || match[0];
            let posInicio: number | undefined;
            let posFim: number | undefined;
            if (match[1] !== undefined && (match as any).indices?.[1]) {
              [posInicio, posFim] = (match as any).indices[1];
            } else if (match[1] !== undefined) {
              posInicio = match.index;
              posFim = match.index + match[0].length;
            } else {
              posInicio = match.index;
              posFim = match.index + match[0].length;
            }
            dados.push({ campo: config.nome_campo, valor, posInicio, posFim });
          } else if (config.obrigatorio) {
            return { success: false, dados: [], erro: `Campo obrigatório "${config.nome_campo}" não encontrado` };
          }
        } catch {
          // Regex invalido, ignorar
        }
      }
    }

    if (dados.length === 0) {
      return { success: false, dados: [], erro: 'Etiqueta não reconhecida pelos modelos configurados' };
    }
    return { success: true, dados };
  };

  // ============================
  // Auto-fill palete number from navigation state (e.g., from dashboard)
  useEffect(() => {
    const state = location.state as { numeroPalete?: number } | null;
    if (state?.numeroPalete && !paleteInfo && !loading) {
      const num = String(state.numeroPalete);
      setNumeroPalete(num);
      // Auto-trigger search
      buscarPalete(parseInt(num));
      // Clear state to avoid re-triggering
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 1: Buscar Palete
  // ============================
  const buscarPalete = async (numPalete: number) => {
    if (!numPalete || numPalete <= 0) {
      setError('Informe um número de palete válido');
      return;
    }

    setError('');
    setLoading(true);

    try {
      let result: any = null;
      try {
        result = await buscarDadosPaleteDescarregamento(numPalete);
      } catch (apiErr: any) {
        // Se offline, tentar cache local
        if (!navigator.onLine) {
          result = await buscarDadosPaleteDescarregamentoOffline(numPalete);
        } else {
          throw apiErr;
        }
      }

      if (!result || !result.success || !result.palete) {
        setError(`Palete #${numPalete} não encontrado em nenhuma operação ativa`);
        setLoading(false);
        return;
      }

      if (!result.cliente) {
        setError('Palete encontrado, mas sem cliente associado');
        setLoading(false);
        return;
      }

      // Verificar se o palete já foi carregado
      if (result.ja_carregado) {
        setError(`Palete #${numPalete} já foi carregado e não pode receber novas etiquetas no pré-carregamento.`);
        setLoading(false);
        return;
      }

      // Save palete info
      setPaleteInfo({
        id: result.palete.id,
        numero_palete: result.palete.numero_palete,
        area_nome: result.palete.area_nome,
        area_codigo: result.palete.area_codigo,
        area_regiao: result.palete.area_regiao,
        operacao_id: result.palete.operacao_id,
        numero_operacao: result.palete.numero_operacao,
        doca: result.palete.doca,
        area_entrega_id: result.palete.area_entrega_id,
        tipo_palete: result.palete.tipo_palete,
        data_agendamento: result.palete.data_agendamento,
      });

      // Save client info
      setCliente(result.cliente);

      // Save etiqueta configs
      setEtiquetaModelos(result.etiqueta_modelos || []);
      setEtiquetaConfigs(result.etiqueta_configs || []);

      // Check if client has label configs
      const temModelos = result.etiqueta_modelos && result.etiqueta_modelos.length > 0;
      const temConfigs = result.etiqueta_configs && result.etiqueta_configs.length > 0;
      if (!temModelos && !temConfigs) {
        setError('Este cliente não possui configurações de etiqueta cadastradas.');
        setLoading(false);
        return;
      }

      // Create conference
      let confId: number | null = null;
      try {
        const confResult = await criarConferencia({
          tipo: 'pre_carregamento',
          cliente_id: result.cliente.id,
          observacoes: `Pré-carregamento - Palete #${numPalete} - ${result.cliente.razao_social}`
        });
        if (confResult.success) {
          confId = confResult.conferencia.id;
        }
      } catch (confErr: any) {
        if (!navigator.onLine) {
          confId = await gerarTempConferenciaId();
          await salvarConferenciaPendente({
            temp_id: confId,
            tipo: 'pre_carregamento',
            cliente_id: result.cliente.id,
            observacoes: `Pré-carregamento - Palete #${numPalete} - ${result.cliente.razao_social}`,
          });
        } else {
          throw new Error(confErr?.message || 'Erro ao criar conferência. Verifique suas permissões ou entre em contato com o administrador.');
        }
      }

      if (!confId) {
        setError('Não foi possível criar a conferência');
        setLoading(false);
        return;
      }

      setConferenciaId(confId);

      // Load existing readings for this palete as history (incluindo duplicadas)
      if (result.leituras && result.leituras.length > 0) {
        const existingItems: HistoricoItem[] = result.leituras.map((l: any) => ({
          id: nextId.current++,
          codigo: l.codigo_lido,
          status: (l.resultado === 'removido' ? 'removida' : l.resultado === 'duplicado' ? 'duplicado' : 'sucesso') as EtiquetaStatus,
          mensagem: l.resultado === 'removido' ? 'Removida do palete' : l.resultado === 'duplicado' ? 'Etiqueta duplicada (leitura anterior)' : 'Leitura anterior',
          timestamp: new Date(l.lido_em),
          leituraId: l.id,
          conferenciaIdOrigem: l.conferencia_id || confId!,
        }));
        setHistorico(existingItems);

        // Popular pedidosVolumes a partir das leituras históricas (sessões anteriores)
        // para que temDivergenciaReal reflita corretamente o estado do palete
        const modelos = result.etiqueta_modelos || [];
        const modeloComVolume = modelos.find((m: any) =>
          m.ativo && m.campo_numero_pedido && m.campo_volume_atual && m.campo_total_volumes
        );
        if (modeloComVolume) {
          const volumesPorPedido: Record<string, { total_volumes: number; volumes_bipados: Set<number>; volume_excedente: boolean; codigos_bipados: Map<number, string> }> = {};
          for (const l of result.leituras) {
            if (l.resultado !== 'ok') continue;
            const dados = l.dados_decodificados;
            if (!dados) continue;
            const numeroPedido = dados[modeloComVolume.campo_numero_pedido!] as string | undefined;
            const volumeAtualStr = dados[modeloComVolume.campo_volume_atual!] as string | undefined;
            const totalVolumesStr = dados[modeloComVolume.campo_total_volumes!] as string | undefined;
            if (!numeroPedido || !volumeAtualStr || !totalVolumesStr) continue;
            const volAtual = parseInt(volumeAtualStr);
            const volTotal = parseInt(totalVolumesStr);
            if (isNaN(volAtual) || isNaN(volTotal) || volTotal <= 0) continue;
            const pedidoKey = String(numeroPedido);
            if (!volumesPorPedido[pedidoKey]) {
              volumesPorPedido[pedidoKey] = { total_volumes: volTotal, volumes_bipados: new Set<number>(), volume_excedente: false, codigos_bipados: new Map<number, string>() };
            }
            volumesPorPedido[pedidoKey].volumes_bipados.add(volAtual);
            volumesPorPedido[pedidoKey].codigos_bipados.set(volAtual, l.codigo_lido);
            volumesPorPedido[pedidoKey].total_volumes = Math.max(volumesPorPedido[pedidoKey].total_volumes, volTotal);
            if (volAtual > volTotal) volumesPorPedido[pedidoKey].volume_excedente = true;
          }
          if (Object.keys(volumesPorPedido).length > 0) {
            setPedidosVolumes(volumesPorPedido);
          }
        }
      }

      // Carregar totais logísticos do palete
      if ((result as any).totais_logisticos) {
        setTotaisLogisticos({
          peso_total: (result as any).totais_logisticos.peso_total || 0,
          cubagem_total: (result as any).totais_logisticos.cubagem_total || 0,
          volumes_total: (result as any).totais_logisticos.volumes_total || 0,
        });
      }

      setEtapa('scanning');
    } catch (err: any) {
      setError(err.message || 'Erro ao buscar palete');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarPalete = (e: React.FormEvent) => {
    e.preventDefault();
    buscarPalete(parseInt(numeroPalete));
  };

  // ============================
  // Gerar Novo Palete - Fluxo unificado
  // O backend determina se precisa selecionar cliente, área, ou cria direto.
  // ============================
  const handleGerarNovoPalete = async (params?: {
    clienteId?: number;
    areaEntregaId?: number;
    tipoPalete?: string;
    dataAgendamento?: string;
  }) => {
    setGerarPaleteLoading(true);
    setError('');

    try {
      const result = isPWA
        ? await gerarNovoPaleteConferente(params)
        : await gerarNovoPaleteRegular(params);

      if (!result.success) {
        setError('Erro ao gerar novo palete');
        setGerarPaleteLoading(false);
        return;
      }

      // Backend pede seleção de cliente
      if (result.precisa_selecionar_cliente && result.clientes) {
        setGerarPaleteClienteModal({ show: true, clientes: result.clientes });
        setGerarPaleteLoading(false);
        return;
      }

      // Backend pede seleção de área de destino
      if (result.precisa_selecionar_area && result.areas && result.cliente) {
        setGerarPaleteAreaModal({
          show: true,
          areas: result.areas,
          clienteId: result.cliente.id
        });
        setGerarPaleteLoading(false);
        return;
      }

      // Backend pede seleção de tipo de palete
      if (result.precisa_selecionar_tipo_palete && result.cliente && result.area_entrega_id) {
        setGerarPaleteTipoModal({
          show: true,
          clienteId: result.cliente.id,
          areaEntregaId: result.area_entrega_id
        });
        setDataAgendamento('');
        setShowAgendamentoInput(false);
        setGerarPaleteLoading(false);
        return;
      }

      // Palete criado com sucesso - finalizar
      await finalizarGeracaoPalete(result);
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar novo palete');
    } finally {
      setGerarPaleteLoading(false);
    }
  };

  // Finalizar geração do palete (após cliente, área e tipo determinados)
  const finalizarGeracaoPalete = async (result: any) => {
    if (!result.palete || !result.cliente) {
      setError('Erro ao gerar palete: dados incompletos');
      setGerarPaleteLoading(false);
      return;
    }

    // Fechar modais se abertos
    setGerarPaleteClienteModal({ show: false, clientes: [] });
    setGerarPaleteAreaModal({ show: false, areas: [], clienteId: 0 });
    setGerarPaleteTipoModal({ show: false, clienteId: 0, areaEntregaId: 0 });
    setShowAgendamentoInput(false);
    setDataAgendamento('');

    // Set palete info
    setPaleteInfo({
      id: result.palete.id,
      numero_palete: result.palete.numero_palete,
      area_nome: result.palete.area_nome,
      area_codigo: result.palete.area_codigo,
      area_regiao: result.palete.area_regiao,
      operacao_id: result.palete.operacao_id,
      numero_operacao: result.palete.numero_operacao,
      doca: result.palete.doca,
      area_entrega_id: result.palete.area_entrega_id,
      tipo_palete: result.palete.tipo_palete,
      data_agendamento: result.palete.data_agendamento,
    });
    setNumeroPalete(String(result.palete.numero_palete));

    // Set client info
    setCliente(result.cliente);

    // Set etiqueta configs
    setEtiquetaModelos(result.etiqueta_modelos || []);
    setEtiquetaConfigs(result.etiqueta_configs || []);

    // Check if client has label configs
    const temModelos = result.etiqueta_modelos && result.etiqueta_modelos.length > 0;
    const temConfigs = result.etiqueta_configs && result.etiqueta_configs.length > 0;
    if (!temModelos && !temConfigs) {
      setError('Este cliente não possui configurações de etiqueta cadastradas.');
      setGerarPaleteLoading(false);
      return;
    }

    // Create conference
    let confId: number | null = null;
    try {
      const confResult = await criarConferencia({
        tipo: 'pre_carregamento',
        cliente_id: result.cliente.id,
        observacoes: `Pré-carregamento - Palete #${result.palete.numero_palete} - ${result.cliente.razao_social}`
      });
      if (confResult.success) {
        confId = confResult.conferencia.id;
      }
    } catch (confErr: any) {
      if (!navigator.onLine) {
        confId = await gerarTempConferenciaId();
        await salvarConferenciaPendente({
          temp_id: confId,
          tipo: 'pre_carregamento',
          cliente_id: result.cliente.id,
          observacoes: `Pré-carregamento - Palete #${result.palete.numero_palete} - ${result.cliente.razao_social}`,
        });
      } else {
        throw new Error(confErr?.message || 'Erro ao criar conferência. Verifique suas permissões ou entre em contato com o administrador.');
      }
    }

    if (!confId) {
      setError('Não foi possível criar a conferência');
      setGerarPaleteLoading(false);
      return;
    }

    setConferenciaId(confId);

    // Skip to scanning
    setEtapa('scanning');
  };

  // Handlers para seleção nos modais
  const handleSelecionarClienteGerarPalete = (clienteId: number) => {
    setGerarPaleteClienteModal({ show: false, clientes: [] });
    handleGerarNovoPalete({ clienteId });
  };

  const handleSelecionarAreaGerarPalete = (areaId: number) => {
    const clienteId = gerarPaleteAreaModal.clienteId;
    setGerarPaleteAreaModal({ show: false, areas: [], clienteId: 0 });
    handleGerarNovoPalete({ clienteId, areaEntregaId: areaId });
  };

  const handleSelecionarTipoPalete = (tipo: 'normal' | 'agendamento', agendamentoData?: string) => {
    const { clienteId, areaEntregaId } = gerarPaleteTipoModal;
    setGerarPaleteTipoModal({ show: false, clienteId: 0, areaEntregaId: 0 });
    setShowAgendamentoInput(false);
    handleGerarNovoPalete({
      clienteId,
      areaEntregaId,
      tipoPalete: tipo,
      dataAgendamento: tipo === 'agendamento' ? agendamentoData : undefined
    });
  };

  // ============================
  // Step 2: Scan Etiqueta (continuous loop)
  // ============================
  const handleScanEtiqueta = async (e: React.FormEvent) => {
    e.preventDefault();
    const codigo = (etiquetaRef.current?.value || '').trim();
    if (!codigo) return;
    if (!conferenciaId || !cliente || !paleteInfo) return;

    setScanLoading(true);
    setUltimoStatus(null);
    setError('');

    try {
      // 1. Parse the label
      const parseResult = parseEtiqueta(codigo);
      if (!parseResult.success) {
        addHistorico({
          codigo,
          status: 'erro_parse',
          mensagem: parseResult.erro || 'Etiqueta não reconhecida',
          timestamp: new Date(),
        });
        setUltimoStatus({ status: 'erro_parse', mensagem: parseResult.erro || 'Etiqueta não reconhecida' });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        etiquetaRef.current?.focus();
        setScanLoading(false);
        return;
      }

      // 2. Build decoded data
      const dadosDecodificados: Record<string, unknown> = {};
      parseResult.dados.forEach(d => {
        dadosDecodificados[d.campo] = d.valor;
      });
      dadosDecodificados['numero_palete'] = paleteInfo.numero_palete;
      dadosDecodificados['area_entrega'] = paleteInfo.area_nome;

      // 4. Register reading via API
      let leituraResult: LeituraResponse;
      try {
        leituraResult = await registrarLeitura(conferenciaId, {
          tipo_leitura: 'manual',
          codigo_lido: codigo,
          dados_decodificados: dadosDecodificados,
          quantidade: 1
        });
      } catch (leitErr: any) {
        if (!navigator.onLine) {
          await salvarLeituraPendente({
            conferencia_id: conferenciaId,
            tipo_leitura: 'manual',
            codigo_lido: codigo,
            quantidade: 1,
            lido_em: new Date().toISOString(),
            dados_decodificados: dadosDecodificados,
          });
          addHistorico({
            codigo,
            status: 'sucesso',
            mensagem: 'Salvo offline',
            timestamp: new Date(),
          });
          setUltimoStatus({ status: 'sucesso', mensagem: 'Etiqueta registrada (offline)' });
          if (etiquetaRef.current) etiquetaRef.current.value = '';
          etiquetaRef.current?.focus();
          setScanLoading(false);
          return;
        }
        // Verificar se o erro é de área de entrega divergente
        const isAreaDivergente = leitErr.message && (
          leitErr.message.includes('Área de entrega divergente') ||
          leitErr.message.includes('não corresponde a nenhuma área de entrega')
        );
        // Verificar se o erro é de palete inutilizado
        const isPaleteInutilizado = leitErr.message && (
          leitErr.message.includes('inutilizado') ||
          leitErr.message.includes('Palete Nao Utilizado')
        );
        const errorStatus = isPaleteInutilizado ? 'erro' : (isAreaDivergente ? 'divergencia' : 'erro');
        addHistorico({
          codigo,
          status: errorStatus,
          mensagem: isPaleteInutilizado
            ? `Palete inutilizado - ${leitErr.message}`
            : (leitErr.message || 'Erro ao registrar'),
          timestamp: new Date(),
        });
        setUltimoStatus({
          status: errorStatus,
          mensagem: isPaleteInutilizado
            ? `Palete inutilizado! ${leitErr.message}`
            : (leitErr.message || 'Erro ao registrar leitura')
        });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        etiquetaRef.current?.focus();
        setScanLoading(false);
        return;
      }

      // 5. Process API response

      // 5a. Etiqueta já existe em outro palete - abrir modal para perguntar se deseja transferir
      if (leituraResult.em_outro_palete && leituraResult.outro_palete) {
        playDuplicateSound();
        setTransferenciaModal({
          show: true,
          codigo,
          numeroPaleteAnterior: leituraResult.outro_palete.numero_palete,
          conferenciaIdOrigem: leituraResult.outro_palete.conferencia_id,
          leituraIdOrigem: leituraResult.outro_palete.leitura_id,
          dadosDecodificados: dadosDecodificados,
        });
        if (etiquetaRef.current) etiquetaRef.current.value = '';
        setScanLoading(false);
        return;
      }

      // 5b. Duplicata no mesmo palete
      if (leituraResult.duplicado) {
        // Extrair dados de volume da etiqueta parseada
        const volumeAtualDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('volume_atual') || d.campo.toLowerCase().includes('volume atual')
        );
        const totalVolumesDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('total_volumes') || d.campo.toLowerCase().includes('total de volumes') || d.campo.toLowerCase().includes('total volumes')
        );
        const numeroPedidoDado = parseResult.dados.find(d =>
          d.campo.toLowerCase().includes('pedido') || d.campo.toLowerCase().includes('numero_pedido')
        );

        // Abrir modal de duplicata ao invés de apenas registrar
        playDuplicateSound();
        setDuplicataOcorrenciaManual('');
        setDuplicataObservacao('');
        setDuplicataModal({
          show: true,
          codigo,
          volumeAtual: volumeAtualDado ? parseInt(volumeAtualDado.valor) : null,
          totalVolumes: totalVolumesDado ? parseInt(totalVolumesDado.valor) : null,
          numeroPedido: numeroPedidoDado?.valor || null,
          leituraId: leituraResult.leitura?.id,
        });
      } else {
        // Acumular totais logísticos da etiqueta bipada
        if (leituraResult.dados_logisticos) {
          const dl = leituraResult.dados_logisticos;
          setTotaisLogisticos(prev => ({
            peso_total: prev.peso_total + (dl.peso_total || 0),
            cubagem_total: prev.cubagem_total + (dl.cubagem || 0),
            volumes_total: prev.volumes_total + (dl.volumes || 0),
          }));

          // Rastrear volumes por pedido para detecção dinâmica de divergências
          if (dl.numero_pedido && dl.total_volumes_pedido) {
            const volumeAtual = dl.volume_atual || 0;
            const totalVolumes = dl.total_volumes_pedido;
            const isExcedente = volumeAtual > totalVolumes;

            setPedidosVolumes(prev => {
              const existing = prev[dl.numero_pedido!] || { total_volumes: totalVolumes, volumes_bipados: new Set<number>(), volume_excedente: false, codigos_bipados: new Map<number, string>() };
              const novosVolumes = new Set(existing.volumes_bipados);
              const novosCodigos = new Map(existing.codigos_bipados);
              if (volumeAtual > 0) {
                novosVolumes.add(volumeAtual);
                novosCodigos.set(volumeAtual, codigo);
              }
              return {
                ...prev,
                [dl.numero_pedido!]: {
                  total_volumes: Math.max(existing.total_volumes, totalVolumes),
                  volumes_bipados: novosVolumes,
                  volume_excedente: existing.volume_excedente || isExcedente,
                  codigos_bipados: novosCodigos,
                },
              };
            });
          }
        }

        // Verificar se o volume atual excede o total de volumes do pedido
        const dl = leituraResult.dados_logisticos;
        if (dl && dl.volume_atual && dl.total_volumes_pedido && dl.volume_atual > dl.total_volumes_pedido) {
          addHistorico({
            codigo,
            status: 'divergencia',
            mensagem: `Volume ${dl.volume_atual} excede o total de ${dl.total_volumes_pedido} volumes do pedido ${dl.numero_pedido || ''}`,
            timestamp: new Date(),
            leituraId: leituraResult.leitura?.id,
            conferenciaIdOrigem: conferenciaId,
          });
          setUltimoStatus({ status: 'divergencia', mensagem: `Divergência: Volume ${dl.volume_atual} é maior que o total de ${dl.total_volumes_pedido} volumes!` });
        } else {
          addHistorico({
            codigo,
            status: 'sucesso',
            mensagem: 'Registrada com sucesso',
            timestamp: new Date(),
            leituraId: leituraResult.leitura?.id,
            conferenciaIdOrigem: conferenciaId,
          });
          setUltimoStatus({ status: 'sucesso', mensagem: 'Etiqueta registrada com sucesso!' });
        }
      }
    } catch (err: any) {
      addHistorico({
        codigo,
        status: 'erro',
        mensagem: err.message || 'Erro inesperado',
        timestamp: new Date(),
      });
      setUltimoStatus({ status: 'erro', mensagem: err.message || 'Erro inesperado' });
    } finally {
      if (etiquetaRef.current) etiquetaRef.current.value = '';
      setScanLoading(false);
      // Garantir foco no input após re-render
      setTimeout(() => etiquetaRef.current?.focus(), 50);
    }
  };

  // ============================
  // Transferência entre paletes
  // ============================
  const handleTransferir = async () => {
    if (!conferenciaId || !transferenciaModal.show) return;
    setTransferenciaLoading(true);
    setError('');

    try {
      // Transferir TODAS as leituras do palete anterior (remover todas as leituras 'ok' da conferência de origem)
      const result = await transferirTodasLeiturasEntrePaletes(conferenciaId, {
        conferencia_id_origem: transferenciaModal.conferenciaIdOrigem,
      });

      if (result.success) {
        const palAnterior = transferenciaModal.numeroPaleteAnterior;
        const totalRemovidas = result.total_removidas || 0;

        // Não limpar o histórico do palete atual — apenas informar sobre a transferência.
        // As leituras já feitas neste palete continuam válidas no banco de dados.
        addHistorico({
          codigo: transferenciaModal.codigo,
          status: 'sucesso',
          mensagem: `${totalRemovidas} etiqueta(s) removida(s) do Palete #${palAnterior || '?'}. Bipe as etiquetas do palete anterior novamente neste palete.`,
          timestamp: new Date(),
        });
        setUltimoStatus({
          status: 'sucesso',
          mensagem: `${totalRemovidas} etiqueta(s) removida(s) do Palete #${palAnterior || '?'}. Bipe-as novamente aqui!`,
        });
      }
    } catch (err: any) {
      addHistorico({
        codigo: transferenciaModal.codigo,
        status: 'erro',
        mensagem: err.message || 'Erro ao transferir etiquetas',
        timestamp: new Date(),
      });
      setUltimoStatus({ status: 'erro', mensagem: err.message || 'Erro ao transferir' });
    } finally {
      setTransferenciaLoading(false);
      setTransferenciaModal({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
      setTimeout(() => etiquetaRef.current?.focus(), 50);
    }
  };

  const handleManterNoPaleteAnterior = () => {
    const palAnterior = transferenciaModal.numeroPaleteAnterior;
    addHistorico({
      codigo: transferenciaModal.codigo,
      status: 'removida',
      mensagem: `Etiqueta já bipada no Palete #${palAnterior || '?'} (não transferida)`,
      timestamp: new Date(),
    });
    setUltimoStatus({ status: 'divergencia', mensagem: `Etiqueta mantida no Palete #${palAnterior || '?'}` });
    setTransferenciaModal({ show: false, codigo: '', numeroPaleteAnterior: null, conferenciaIdOrigem: 0, leituraIdOrigem: 0, dadosDecodificados: {} });
    setTimeout(() => etiquetaRef.current?.focus(), 50);
  };

  // ============================
  // Remoção de etiqueta individual
  // ============================
  const handleAbrirRemocao = (item: HistoricoItem) => {
    if (!item.leituraId || !item.conferenciaIdOrigem) return;
    setRemocaoModal({
      show: true,
      historicoItemId: item.id,
      codigo: item.codigo,
      leituraId: item.leituraId,
      conferenciaIdOrigem: item.conferenciaIdOrigem,
    });
  };

  const handleConfirmarRemocao = async () => {
    if (!remocaoModal.show) return;
    setRemocaoLoading(true);
    setError('');

    try {
      const result = await removerLeitura(
        remocaoModal.conferenciaIdOrigem,
        remocaoModal.leituraId,
        'Etiqueta removida pelo conferente para liberá-la para outro palete'
      );

      if (result.success) {
        // Manter a etiqueta no histórico mas com status 'removida'
        setHistorico(prev => prev.map(h =>
          h.id === remocaoModal.historicoItemId
            ? { ...h, status: 'removida' as EtiquetaStatus, mensagem: 'Removida do palete - liberada para outro palete' }
            : h
        ));

        // Atualizar pedidosVolumes removendo o volume da etiqueta removida
        // Usar mapeamento do modelo de etiqueta para encontrar campos corretamente
        const parseRemocao = parseEtiqueta(remocaoModal.codigo);
        if (parseRemocao.success) {
          const modeloComVolume = (etiquetaModelos || []).find((m: any) =>
            m.ativo && m.campo_numero_pedido && m.campo_volume_atual
          );

          let numeroPedido: string | null = null;
          let volumeAtualStr: string | null = null;

          if (modeloComVolume) {
            // Usar campos mapeados do modelo (método confiável)
            const pedidoDado = parseRemocao.dados.find(d => d.campo === modeloComVolume.campo_numero_pedido);
            const volumeDado = parseRemocao.dados.find(d => d.campo === modeloComVolume.campo_volume_atual);
            numeroPedido = pedidoDado?.valor || null;
            volumeAtualStr = volumeDado?.valor || null;
          }

          // Fallback: busca por padrão nos nomes dos campos
          if (!numeroPedido) {
            const pedidoDado = parseRemocao.dados.find(d =>
              d.campo.toLowerCase().includes('pedido') || d.campo.toLowerCase().includes('numero_pedido')
            );
            numeroPedido = pedidoDado?.valor || null;
          }
          if (!volumeAtualStr) {
            const volumeDado = parseRemocao.dados.find(d =>
              d.campo.toLowerCase().includes('volume_atual') || d.campo.toLowerCase().includes('volume atual')
            );
            volumeAtualStr = volumeDado?.valor || null;
          }

          if (numeroPedido && volumeAtualStr) {
            const volRemovido = parseInt(volumeAtualStr);
            if (!isNaN(volRemovido)) {
              setPedidosVolumes(prev => {
                const existing = prev[numeroPedido!];
                if (!existing) return prev;
                const novosVolumes = new Set(existing.volumes_bipados);
                novosVolumes.delete(volRemovido);
                const novosCodigos = new Map(existing.codigos_bipados);
                novosCodigos.delete(volRemovido);
                if (novosVolumes.size === 0) {
                  const { [numeroPedido!]: _, ...rest } = prev;
                  return rest;
                }
                return { ...prev, [numeroPedido!]: { ...existing, volumes_bipados: novosVolumes, codigos_bipados: novosCodigos } };
              });
            }
          }
        }

        // Recarregar totais logísticos do backend para manter sincronizado
        if (conferenciaId) {
          try {
            const totaisResult = await buscarTotaisLogisticos(conferenciaId, paleteInfo?.numero_palete);
            if (totaisResult.success) {
              setTotaisLogisticos({
                peso_total: totaisResult.totais_logisticos.peso_total || 0,
                cubagem_total: totaisResult.totais_logisticos.cubagem_total || 0,
                volumes_total: totaisResult.totais_logisticos.volumes_total || 0,
              });
            }
          } catch {
            // Não bloquear a operação se falhar ao recarregar totais
          }
        }

        setUltimoStatus({ status: 'sucesso', mensagem: `Etiqueta removida com sucesso. Agora pode ser bipada em outro palete.` });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao remover etiqueta');
      setUltimoStatus({ status: 'erro', mensagem: err.message || 'Erro ao remover etiqueta' });
    } finally {
      setRemocaoLoading(false);
      setRemocaoModal({ show: false, historicoItemId: 0, codigo: '', leituraId: 0, conferenciaIdOrigem: 0 });
      setTimeout(() => etiquetaRef.current?.focus(), 50);
    }
  };

  // ============================
  // Remover todas as etiquetas de um pedido
  // ============================
  const handleConfirmarRemocaoPedido = async () => {
    if (!remocaoPedidoModal.show || !conferenciaId) return;
    setRemocaoPedidoLoading(true);
    setError('');

    try {
      const result = await removerLeiturasPedidosDivergentes(conferenciaId, {
        numero_pedidos: [remocaoPedidoModal.numeroPedido],
        motivo: 'Pedido removido manualmente pelo conferente',
      });

      if (result.success) {
        // Marcar todas as leituras deste pedido como removidas no histórico
        const pedidoKey = remocaoPedidoModal.numeroPedido;
        const pedidoInfo = pedidosVolumes[pedidoKey];
        if (pedidoInfo) {
          const codigosBipados = new Set(Array.from(pedidoInfo.codigos_bipados.values()));
          setHistorico(prev => prev.map(h =>
            codigosBipados.has(h.codigo) && h.status !== 'removida'
              ? { ...h, status: 'removida' as EtiquetaStatus, mensagem: 'Removida - pedido excluído do palete' }
              : h
          ));
        }

        // Remover o pedido inteiro do estado pedidosVolumes
        setPedidosVolumes(prev => {
          const { [pedidoKey]: _, ...rest } = prev;
          return rest;
        });

        // Recarregar totais logísticos do backend para manter sincronizado
        try {
          const totaisResult = await buscarTotaisLogisticos(conferenciaId, paleteInfo?.numero_palete);
          if (totaisResult.success) {
            setTotaisLogisticos({
              peso_total: totaisResult.totais_logisticos.peso_total || 0,
              cubagem_total: totaisResult.totais_logisticos.cubagem_total || 0,
              volumes_total: totaisResult.totais_logisticos.volumes_total || 0,
            });
          }
        } catch {
          // Não bloquear a operação se falhar ao recarregar totais
        }

        setUltimoStatus({ status: 'sucesso', mensagem: `Pedido ${pedidoKey} removido com sucesso (${result.total_removidas} etiqueta(s))` });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao remover pedido');
      setUltimoStatus({ status: 'erro', mensagem: err.message || 'Erro ao remover pedido' });
    } finally {
      setRemocaoPedidoLoading(false);
      setRemocaoPedidoModal({ show: false, numeroPedido: '', totalVolumes: 0, volumesBipados: 0 });
      setTimeout(() => etiquetaRef.current?.focus(), 50);
    }
  };

  // ============================
  // Step 3: Analisar Divergências
  // ============================
  const handleAnalisarDivergencias = async () => {
    if (!conferenciaId) return;

    setDivergenciaLoading(true);
    setError('');

    try {
      const result = await analisarDivergenciasVolumes(conferenciaId);

      if (!result.success) {
        setError('Erro ao analisar divergências de volumes');
        setDivergenciaLoading(false);
        return;
      }

      const pedidosIncompletos = result.analise_volumes.filter(
        (p: AnaliseVolumePedido) => p.status === 'incompleto'
      );

      if (pedidosIncompletos.length === 0) {
        setError('');
        setAnaliseVolumes(result.analise_volumes);
        setEtapa('divergencias');
        setDivergenciaLoading(false);
        return;
      }

      // Initialize forms for each divergent order
      const forms: Record<string, DivergenciaFormData> = {};
      for (const pedido of pedidosIncompletos) {
        // Selecionar todas as etiquetas e faltantes por padrão
        const allEtiquetaIndices = new Set(pedido.leituras.map((_: any, idx: number) => idx));
        const allFaltantes = new Set(pedido.volumes_faltantes);
        forms[pedido.numero_pedido] = {
          pedidoKey: pedido.numero_pedido,
          ocorrencia_tipo: tiposOcorrencia.length > 0 ? tiposOcorrencia[0].value : 'volume_faltante',
          justificativa: '',
          selected: true,
          selectedEtiquetas: allEtiquetaIndices,
          selectedFaltantes: allFaltantes,
        };
      }

      setAnaliseVolumes(result.analise_volumes);
      setDivergenciaForms(forms);
      setEtapa('divergencias');
    } catch (err: any) {
      setError(err.message || 'Erro ao analisar divergências');
    } finally {
      setDivergenciaLoading(false);
    }
  };

  // Update individual divergence form (only for string/boolean fields)
  const updateDivergenciaForm = (pedidoKey: string, field: 'ocorrencia_tipo' | 'justificativa' | 'selected', value: string | boolean) => {
    setDivergenciaForms(prev => ({
      ...prev,
      [pedidoKey]: { ...prev[pedidoKey], [field]: value }
    }));
  };

  // Toggle all divergences selection
  const toggleAllDivergencias = (selected: boolean) => {
    setDivergenciaForms(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        updated[key] = { ...updated[key], selected };
      }
      return updated;
    });
  };

  // Toggle etiqueta selection for a specific order
  const toggleEtiqueta = (pedidoKey: string, idx: number) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = new Set(form.selectedEtiquetas);
      if (newSet.has(idx)) newSet.delete(idx); else newSet.add(idx);
      return { ...prev, [pedidoKey]: { ...form, selectedEtiquetas: newSet } };
    });
  };

  // Toggle faltante volume selection for a specific order
  const toggleFaltante = (pedidoKey: string, vol: number) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = new Set(form.selectedFaltantes);
      if (newSet.has(vol)) newSet.delete(vol); else newSet.add(vol);
      return { ...prev, [pedidoKey]: { ...form, selectedFaltantes: newSet } };
    });
  };

  // Toggle all etiquetas for a specific order
  const toggleAllEtiquetas = (pedidoKey: string, selectAll: boolean, totalLeituras: number) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = selectAll ? new Set(Array.from({ length: totalLeituras }, (_, i) => i)) : new Set<number>();
      return { ...prev, [pedidoKey]: { ...form, selectedEtiquetas: newSet } };
    });
  };

  // Toggle all faltantes for a specific order
  const toggleAllFaltantes = (pedidoKey: string, selectAll: boolean, volumesFaltantes: number[]) => {
    setDivergenciaForms(prev => {
      const form = prev[pedidoKey];
      if (!form) return prev;
      const newSet = selectAll ? new Set(volumesFaltantes) : new Set<number>();
      return { ...prev, [pedidoKey]: { ...form, selectedFaltantes: newSet } };
    });
  };

  // Helper: verifica se o tipo de ocorrência ativo para um pedido é de corte
  const isTipoCorteParaPedido = (form: DivergenciaFormData): boolean => {
    const tipo = modoOcorrenciaUnica ? ocorrenciaUnicaTipo : form.ocorrencia_tipo;
    return tiposOcorrencia.find(t => t.value === tipo)?.ocorrencia_de_corte || false;
  };

  // Save divergence occurrences
  const handleSalvarDivergencias = async () => {
    if (!conferenciaId || !cliente) return;

    setDivergenciaSaving(true);
    setError('');

    try {
      const pedidosIncompletos = analiseVolumes.filter(p => p.status === 'incompleto');
      const selectedForms = Object.values(divergenciaForms).filter(f => f.selected);

      if (selectedForms.length === 0) {
        setError('Selecione ao menos uma divergência para registrar');
        setDivergenciaSaving(false);
        return;
      }

      // Validar que cada form selecionado tem ao menos uma etiqueta ou faltante
      for (const form of selectedForms) {
        const isCorte = isTipoCorteParaPedido(form);
        if (isCorte) {
          // Para corte, precisa de ao menos um volume faltante
          if (form.selectedFaltantes.size === 0) {
            setError(`Pedido ${form.pedidoKey}: selecione ao menos um volume faltante para o ponto de corte`);
            setDivergenciaSaving(false);
            return;
          }
        } else {
          if (form.selectedEtiquetas.size === 0 && form.selectedFaltantes.size === 0) {
            setError(`Pedido ${form.pedidoKey}: selecione ao menos uma etiqueta ou volume faltante`);
            setDivergenciaSaving(false);
            return;
          }
        }
      }

      const ocorrencias: DivergenciaOcorrenciaCreateData[] = selectedForms.map(form => {
        const pedido = pedidosIncompletos.find(p => p.numero_pedido === form.pedidoKey);
        const isCorte = isTipoCorteParaPedido(form);

        // Build etiquetas list
        const etiquetas: DivergenciaOcorrenciaCreateData['etiquetas'] = [];

        // Para corte: as etiquetas já bipadas NÃO entram na ocorrência
        // (o sistema identifica automaticamente via conferencia_leituras)
        if (!isCorte && pedido?.leituras) {
          pedido.leituras.forEach((l, idx) => {
            if (form.selectedEtiquetas.has(idx)) {
              etiquetas.push({
                codigo_lido: l.codigo_lido,
                volume_atual: l.volume_atual,
                total_volumes: l.total_volumes,
                lido_em: l.lido_em,
                status: 'conferido',
              });
            }
          });
        }

        // Add selected missing volumes as "faltante" entries
        if (pedido?.volumes_faltantes) {
          for (const volFaltante of pedido.volumes_faltantes) {
            if (isCorte || form.selectedFaltantes.has(volFaltante)) {
              // Para corte: sempre inclui TODOS os faltantes (independente da seleção)
              etiquetas.push({
                codigo_lido: `FALTANTE - Pedido ${form.pedidoKey} - Volume ${volFaltante} de ${pedido.total_volumes}`,
                volume_atual: volFaltante,
                total_volumes: pedido.total_volumes,
                status: 'faltante',
              });
            }
          }
        }

        return {
          conferencia_id: conferenciaId,
          cliente_id: cliente.id,
          numero_pedido: form.pedidoKey,
          ocorrencia_tipo: modoOcorrenciaUnica ? ocorrenciaUnicaTipo : form.ocorrencia_tipo,
          justificativa: modoOcorrenciaUnica ? ocorrenciaUnicaJustificativa : form.justificativa,
          total_volumes_esperado: pedido?.total_volumes || 0,
          total_volumes_conferido: pedido?.quantidade_conferida || 0,
          total_volumes_faltante: pedido?.quantidade_faltante || 0,
          volumes_conferidos: pedido?.volumes_conferidos || [],
          volumes_faltantes: pedido?.volumes_faltantes || [],
          etiquetas,
        };
      });

      const result = await criarDivergenciaOcorrenciaBatch(ocorrencias);

      if (result.success) {
        // Verificar se alguma ocorrência é ponto de corte
        const temPontoCorte = result.tem_ponto_corte || false;

        if (temPontoCorte) {
          // Ponto de corte: NÃO remover leituras do pallet.
          // O pallet mantém apenas as etiquetas já bipadas.
          // As etiquetas faltantes serão geradas em novos pallets.
          setPedidosDivergentesRemovidos(selectedForms.map(f => f.pedidoKey));
          setTotalLeiturasRemovidas(0);
        } else {
          // Divergência normal: remover as leituras dos pedidos divergentes do palete
          // para que o palete não fique mais divergente e possa ser usado no carregamento
          const pedidosParaRemover = selectedForms.map(f => f.pedidoKey);
          try {
            const remocaoResult = await removerLeiturasPedidosDivergentes(conferenciaId, {
              numero_pedidos: pedidosParaRemover,
              motivo: 'Pedido divergente - ocorrência registrada, leituras removidas para liberar palete para carregamento'
            });
            setPedidosDivergentesRemovidos(pedidosParaRemover);
            setTotalLeiturasRemovidas(remocaoResult.total_removidas || 0);
          } catch {
            // Mesmo se a remoção falhar, as ocorrências já foram salvas
          }
        }
        setDivergenciaSuccess(true);
      } else {
        setError('Erro ao salvar ocorrências de divergência');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar ocorrências');
    } finally {
      setDivergenciaSaving(false);
    }
  };

  // Reset
  const handleReset = () => {
    setNumeroPalete('');
    setPaleteInfo(null);
    setCliente(null);
    setEtiquetaModelos([]);
    setEtiquetaConfigs([]);
    if (etiquetaRef.current) etiquetaRef.current.value = '';
    setConferenciaId(null);
    setHistorico([]);
    setUltimoStatus(null);
    setError('');
    setAnaliseVolumes([]);
    setDivergenciaForms({});
    setDivergenciaSuccess(false);
    setPedidosDivergentesRemovidos([]);
    setTotalLeiturasRemovidas(0);
    setModoOcorrenciaUnica(false);
    setOcorrenciaUnicaTipo(tiposOcorrencia.length > 0 ? tiposOcorrencia[0].value : 'volume_faltante');
    setOcorrenciaUnicaJustificativa('');
    setExpandedPedido(null);
    setTotaisLogisticos({ peso_total: 0, cubagem_total: 0, volumes_total: 0 });
    setPedidosVolumes({});
    setGerarPaleteLoading(false);
    setGerarPaleteClienteModal({ show: false, clientes: [] });
    setGerarPaleteAreaModal({ show: false, areas: [], clienteId: 0 });
    setGerarPaleteTipoModal({ show: false, clienteId: 0, areaEntregaId: 0 });
    setShowAgendamentoInput(false);
    setDataAgendamento('');
    if (isPWA) {
      navigate(backUrl);
      return;
    }
    setEtapa('palete');
  };

  // Tratar resposta do modal de duplicata (modo automático)
  const handleDuplicataResponseAuto = async (tipo: 'erro_conferente' | 'duplicata_cliente') => {
    if (!conferenciaId || !cliente) return;

    const { codigo, volumeAtual, totalVolumes, numeroPedido } = duplicataModal;

    setDuplicataLoading(true);
    try {
      const tipoEncontrado = todosOcorrenciaTipos.find(t => t.automatico && t.tipo_automatico === tipo);
      if (!tipoEncontrado) {
        utils.showNotification(
          `Tipo de ocorrência automática "${tipo === 'erro_conferente' ? 'Erro do Conferente' : 'Produto Duplicado'}" não está cadastrado. Cadastre-o na página de Ocorrências.`,
          { type: 'error' }
        );
        setDuplicataLoading(false);
        return;
      }
      const ocorrenciaTipo = tipoEncontrado.codigo;
      const justificativa = tipo === 'erro_conferente'
        ? `[Ocorrência automática] Erro do conferente: etiqueta ${codigo} bipada em duplicidade`
        : `[Ocorrência automática] Cliente enviou produto duplicado com a mesma etiqueta: ${codigo}`;

      await criarDivergenciaOcorrenciaBatch([{
        conferencia_id: conferenciaId,
        cliente_id: cliente.id,
        numero_pedido: numeroPedido || '',
        ocorrencia_tipo: ocorrenciaTipo,
        justificativa,
        total_volumes_esperado: totalVolumes || 0,
        total_volumes_conferido: volumeAtual || 0,
        total_volumes_faltante: 0,
        volumes_conferidos: [],
        volumes_faltantes: [],
        etiquetas: [{
          codigo_lido: codigo,
          volume_atual: volumeAtual || 0,
          total_volumes: totalVolumes || 0,
          status: 'duplicado',
        }],
      }]);
    } catch {
      // Erro ao registrar ocorrência - não crítico
    } finally {
      setDuplicataLoading(false);
    }

    const mensagem = tipo === 'erro_conferente'
      ? 'Duplicada - Erro do conferente'
      : 'Duplicada - Cliente enviou duplicado';

    addHistorico({ codigo, status: 'duplicado', mensagem, timestamp: new Date() });
    setUltimoStatus({ status: 'duplicado', mensagem });

    setDuplicataModal({ show: false, codigo: '' });
    setDuplicataOcorrenciaManual('');
    setDuplicataObservacao('');
    setTimeout(() => etiquetaRef.current?.focus(), 50);
  };

  // Tratar resposta do modal de duplicata (modo manual - sem automáticos)
  const handleDuplicataResponseManual = async () => {
    if (!conferenciaId || !cliente) return;
    if (!duplicataOcorrenciaManual) {
      utils.showNotification('Selecione um tipo de ocorrência', { type: 'error' });
      return;
    }

    const { codigo, volumeAtual, totalVolumes, numeroPedido } = duplicataModal;
    const tipoSelecionado = todosOcorrenciaTipos.find(t => t.codigo === duplicataOcorrenciaManual);
    const observacao = duplicataObservacao.trim() || `Etiqueta duplicada: ${codigo}`;

    setDuplicataLoading(true);
    try {
      await criarDivergenciaOcorrenciaBatch([{
        conferencia_id: conferenciaId,
        cliente_id: cliente.id,
        numero_pedido: numeroPedido || '',
        ocorrencia_tipo: duplicataOcorrenciaManual,
        justificativa: observacao,
        total_volumes_esperado: totalVolumes || 0,
        total_volumes_conferido: volumeAtual || 0,
        total_volumes_faltante: 0,
        volumes_conferidos: [],
        volumes_faltantes: [],
        etiquetas: [{
          codigo_lido: codigo,
          volume_atual: volumeAtual || 0,
          total_volumes: totalVolumes || 0,
          status: 'duplicado',
        }],
      }]);
    } catch {
      // Erro ao registrar ocorrência - não crítico
    } finally {
      setDuplicataLoading(false);
    }

    const mensagem = `Duplicada - ${tipoSelecionado?.nome || duplicataOcorrenciaManual}`;
    addHistorico({ codigo, status: 'duplicado', mensagem, timestamp: new Date() });
    setUltimoStatus({ status: 'duplicado', mensagem });

    setDuplicataModal({ show: false, codigo: '' });
    setDuplicataOcorrenciaManual('');
    setDuplicataObservacao('');
    setTimeout(() => etiquetaRef.current?.focus(), 50);
  };

  const pedidosIncompletos = analiseVolumes.filter(p => p.status === 'incompleto');
  const pedidosCompletos = analiseVolumes.filter(p => p.status === 'completo');

  return (
    <div className={isPWA ? 'pwa-page-wrapper' : ''}>
      {isPWA && (
        <div className="pwa-green-header">
          <button className="pwa-header-back" onClick={() => navigate(backUrl)} aria-label="Voltar ao dashboard">
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="pwa-header-info">
            <span className="pwa-header-title">Pré-Carregamento</span>
            {contadores.total > 0 && (
              <span className="pwa-header-badge">{contadores.total} bipada{contadores.total !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      )}
      <div className={isPWA ? 'page-container pwa-content' : 'page-container'}>
      {!isPWA && (
        <button className="conferencia-back-link" onClick={() => navigate(backUrl)}>
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      )}

      <div className="precarreg-container">
        {!isPWA && (
        <div className="precarreg-header">
          <div className="precarreg-title">
            <i className="fas fa-box-open"></i>
            <h2>Pré-carregamento</h2>
          </div>
          {contadores.total > 0 && (
            <div className="precarreg-counter">
              <i className="fas fa-barcode"></i>
              <span>{contadores.total} bipada{contadores.total !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        )}

        {/* Step indicator */}
        <div className="precarreg-steps">
          <div className={`precarreg-step active ${etapa === 'scanning' || etapa === 'divergencias' ? 'completed' : ''}`}>
            <div className="step-circle">
              {etapa === 'scanning' || etapa === 'divergencias' ? <i className="fas fa-check"></i> : 1}
            </div>
            <span className="step-label">{getModoPreCarregamento() === 'lista_paletes' ? 'Seleção' : 'Palete'}</span>
            <div className="step-line" />
          </div>
          <div className={`precarreg-step ${etapa === 'scanning' ? 'active' : ''} ${etapa === 'divergencias' ? 'completed' : ''}`}>
            <div className="step-circle">
              {etapa === 'divergencias' ? <i className="fas fa-check"></i> : 2}
            </div>
            <span className="step-label">Bipagem</span>
            <div className="step-line" />
          </div>
          <div className={`precarreg-step ${etapa === 'divergencias' ? 'active' : ''}`}>
            <div className="step-circle">3</div>
            <span className="step-label">Divergências</span>
          </div>
        </div>

        {error && (
          <div className="precarreg-alert error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}

        {/* ============================
            Step 1: Número do Palete
            ============================ */}
        {etapa === 'palete' && getModoPreCarregamento() === 'palete_individual' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <i className="fas fa-pallet"></i>
              <h3>Número do Palete</h3>
            </div>
            <p className="precarreg-description">
              Informe o número do palete ou toque em <strong>+</strong> para gerar um novo automaticamente.
            </p>
            <form onSubmit={handleBuscarPalete}>
              <div className="precarreg-input-group">
                <label>Palete</label>
                <div className="precarreg-input-with-action precarreg-input-dual-actions">
                  <input
                    ref={paleteRef}
                    type="number"
                    inputMode={showKeyboard ? 'numeric' : 'none'}
                    className="precarreg-input"
                    value={numeroPalete}
                    onChange={(e) => setNumeroPalete(e.target.value)}
                    placeholder="Número do palete"
                    autoFocus
                    min="1"
                  />
                  <button
                    type="button"
                    className={`precarreg-keyboard-toggle precarreg-keyboard-toggle-secondary ${showKeyboard ? 'active' : ''}`}
                    onClick={() => {
                      setShowKeyboard(!showKeyboard);
                      setTimeout(() => {
                        if (paleteRef.current) {
                          paleteRef.current.blur();
                          paleteRef.current.focus();
                        }
                      }, 100);
                    }}
                    title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                  >
                    <i className="fas fa-keyboard"></i>
                  </button>
                  <button
                    type="button"
                    className="precarreg-input-action-btn"
                    title="Gerar novo palete"
                    disabled={loading || gerarPaleteLoading}
                    onClick={() => handleGerarNovoPalete(clienteConferenteSelecionado ? { clienteId: clienteConferenteSelecionado } : undefined)}
                  >
                    {gerarPaleteLoading
                      ? <i className="fas fa-spinner fa-spin"></i>
                      : <i className="fas fa-plus"></i>
                    }
                  </button>
                </div>
              </div>
              <button type="submit" className="precarreg-btn primary" disabled={loading || gerarPaleteLoading || !numeroPalete}>
                {loading ? <><i className="fas fa-spinner fa-spin"></i> Buscando...</> : <><i className="fas fa-search"></i> Buscar Palete</>}
              </button>
            </form>
          </div>
        )}

        {/* ============================
            Step 1 (modo lista): Lista de Paletes
            ============================ */}
        {etapa === 'palete' && getModoPreCarregamento() === 'lista_paletes' && (
          <div className="precarreg-card">
            <div className="precarreg-card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <i className="fas fa-list-check"></i>
                <h3>Paletes Disponíveis</h3>
              </div>
            </div>
            <p className="precarreg-description">
              Selecione um palete da montagem de carga para iniciar o pré-carregamento, ou toque em <strong>+</strong> para gerar um novo.
            </p>

            {/* Filtro de cliente quando conferente tem múltiplos clientes vinculados */}
            {clientesConferente.length > 1 && !clienteConferenteSelecionado && (
              <div style={{
                padding: '12px 14px', background: '#fff8e1', borderRadius: 8,
                border: '1px solid #ffe082', marginBottom: 16
              }}>
                <label style={{
                  display: 'block', fontSize: '0.8rem', fontWeight: 600,
                  color: '#f57f17', marginBottom: 8, textTransform: 'uppercase'
                }}>
                  <i className="fas fa-filter" style={{ marginRight: 6 }}></i>
                  Selecione o cliente
                </label>
                <select
                  className="precarreg-input"
                  value={clienteConferenteSelecionado || ''}
                  onChange={(e) => {
                    const id = e.target.value ? parseInt(e.target.value) : null;
                    setClienteConferenteSelecionado(id);
                    if (id) {
                      carregarListaPaletesPreCarregamento(id);
                    } else {
                      setPaletesLista([]);
                    }
                  }}
                  style={{ padding: '10px 12px', fontSize: '0.9rem', width: '100%' }}
                >
                  <option value="">-- Selecione um cliente --</option>
                  {clientesConferente.map(c => (
                    <option key={c.id} value={c.id}>{c.razao_social} ({c.cnpj})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Info do cliente selecionado com opção de trocar */}
            {clienteConferenteSelecionado && clientesConferente.length > 0 && (() => {
              const sel = clientesConferente.find(c => c.id === clienteConferenteSelecionado);
              return sel ? (
                <div style={{
                  padding: '8px 14px', background: '#e3f2fd', borderRadius: 8,
                  marginBottom: 12, fontSize: '0.85rem', color: '#1565c0',
                  display: 'flex', alignItems: 'center', gap: 8
                }}>
                  <i className="fas fa-building"></i>
                  <strong style={{ flex: 1 }}>{sel.razao_social}</strong>
                  {clientesConferente.length > 1 && (
                    <button
                      onClick={() => {
                        setClienteConferenteSelecionado(null);
                        setPaletesLista([]);
                        setAreaCodeInput('');
                        setAreaCodeError('');
                      }}
                      style={{
                        background: 'none', border: '1px solid #90caf9', borderRadius: 6,
                        color: '#1565c0', cursor: 'pointer', fontSize: '0.75rem',
                        padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <i className="fas fa-exchange-alt" style={{ fontSize: '0.7rem' }}></i>
                      Trocar
                    </button>
                  )}
                </div>
              ) : null;
            })()}

            {/* Conferente sem vínculos */}
            {semVinculosConferente ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#c62828' }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', marginBottom: 10, display: 'block' }}></i>
                <p style={{ fontWeight: 600 }}>Nenhum cliente vinculado ao seu cadastro</p>
                <p style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: 8 }}>
                  Para visualizar paletes, solicite ao administrador que vincule seu usuário aos clientes desejados.
                </p>
              </div>
            ) : loadingLista || loadingClientesConferente ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '1.5rem', color: '#3498db' }}></i>
                <p style={{ marginTop: 10, color: '#7f8c8d' }}>
                  {loadingClientesConferente ? 'Verificando clientes vinculados...' : 'Carregando paletes...'}
                </p>
              </div>
            ) : !clienteConferenteSelecionado && clientesConferente.length > 1 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#7f8c8d' }}>
                <i className="fas fa-hand-pointer" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block' }}></i>
                <p>Selecione um cliente acima para visualizar os paletes</p>
              </div>
            ) : paletesLista.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#7f8c8d' }}>
                <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: 10, display: 'block' }}></i>
                <p>Nenhum palete disponível para pré-carregamento</p>
                <p style={{ fontSize: '0.85rem', marginTop: 8 }}>
                  Verifique se há uma montagem de carga ativa ou gere um novo palete.
                </p>
                <button
                  type="button"
                  className="precarreg-input-action-btn"
                  title="Gerar novo palete"
                  disabled={loading || gerarPaleteLoading}
                  onClick={() => handleGerarNovoPalete(clienteConferenteSelecionado ? { clienteId: clienteConferenteSelecionado } : undefined)}
                  style={{ position: 'static', transform: 'none', margin: '12px auto 0', width: 36, height: 36 }}
                >
                  {gerarPaleteLoading
                    ? <i className="fas fa-spinner fa-spin" style={{ color: '#fff' }}></i>
                    : <i className="fas fa-plus" style={{ color: '#fff' }}></i>
                  }
                </button>
              </div>
            ) : (
              <>
                {/* Resumo */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', fontSize: '0.85rem', alignItems: 'center' }}>
                  <span style={{ padding: '4px 10px', background: '#f0f0f0', borderRadius: 6 }}>
                    Total: <strong>{paletesLista.length}</strong>
                  </span>
                  <button
                    type="button"
                    className="precarreg-input-action-btn"
                    title="Gerar novo palete"
                    disabled={loading || gerarPaleteLoading}
                    onClick={() => handleGerarNovoPalete(clienteConferenteSelecionado ? { clienteId: clienteConferenteSelecionado } : undefined)}
                    style={{ position: 'static', transform: 'none', marginLeft: 'auto', width: 30, height: 30, fontSize: '0.85rem' }}
                  >
                    {gerarPaleteLoading
                      ? <i className="fas fa-spinner fa-spin" style={{ color: '#fff' }}></i>
                      : <i className="fas fa-plus" style={{ color: '#fff' }}></i>
                    }
                  </button>
                </div>

                {/* Input para bipar código da área de entrega */}
                <div className="precarreg-input-group" style={{ marginBottom: 16 }}>
                  <label><i className="fas fa-map-marker-alt" style={{ marginRight: 6 }}></i>Área de entrega</label>
                  <div className="precarreg-input-with-action">
                    <input
                      ref={areaCodeRef}
                      type="text"
                      inputMode={showAreaKeyboard ? 'text' : 'none'}
                      className="precarreg-input mono"
                      placeholder="Bipe o código da área de entrega..."
                      value={areaCodeInput}
                      onChange={(e) => {
                        setAreaCodeInput(e.target.value);
                        setAreaCodeError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleBuscarPorAreaCode(areaCodeInput);
                        }
                      }}
                      disabled={loading}
                      style={areaCodeError ? { borderColor: '#e74c3c' } : undefined}
                    />
                    {configGlobal?.habilitar_camera_scanner ? (
                      <div className="precarreg-scan-options-wrapper" ref={areaScanOptionsRef}>
                        <button
                          type="button"
                          className={`precarreg-scan-options-btn ${showAreaScanOptions ? 'open' : ''}`}
                          onClick={() => setShowAreaScanOptions(!showAreaScanOptions)}
                          title="Opções de entrada"
                        >
                          <i className={`fas ${showAreaScanOptions ? 'fa-times' : 'fa-plus'}`}></i>
                        </button>
                        {showAreaScanOptions && (
                          <div className="precarreg-scan-options-menu">
                            <button
                              type="button"
                              onClick={() => {
                                setShowAreaKeyboard(!showAreaKeyboard);
                                setShowAreaScanOptions(false);
                                setTimeout(() => {
                                  if (areaCodeRef.current) {
                                    areaCodeRef.current.blur();
                                    areaCodeRef.current.focus();
                                  }
                                }, 100);
                              }}
                            >
                              <i className={`fas fa-keyboard scan-option-icon-keyboard ${showAreaKeyboard ? 'active' : ''}`}></i>
                              {showAreaKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAreaScanOptions(false);
                                setShowAreaCameraScanner(true);
                              }}
                              disabled={loading}
                            >
                              <i className="fas fa-camera scan-option-icon-camera"></i>
                              Escanear com câmera
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`precarreg-keyboard-toggle ${showAreaKeyboard ? 'active' : ''}`}
                        onClick={() => {
                          setShowAreaKeyboard(!showAreaKeyboard);
                          setTimeout(() => {
                            if (areaCodeRef.current) {
                              areaCodeRef.current.blur();
                              areaCodeRef.current.focus();
                            }
                          }, 100);
                        }}
                        title={showAreaKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                      >
                        <i className="fas fa-keyboard"></i>
                      </button>
                    )}
                  </div>
                  {areaCodeError && (
                    <div style={{
                      marginTop: 6, fontSize: '0.82rem', color: '#e74c3c',
                      display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {areaCodeError}
                    </div>
                  )}
                </div>

                {/* Lista de paletes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '50vh', overflowY: 'auto' }}>
                  {paletesLista.map(palete => (
                    <button
                      key={palete.id}
                      type="button"
                      onClick={() => handleSelecionarPaleteLista(palete)}
                      disabled={loading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px', background: '#fff', border: '1px solid #e0e0e0',
                        borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer',
                        textAlign: 'left', transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8e44ad'; e.currentTarget.style.background = '#faf5ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = '#fff'; }}
                    >
                      <div style={{
                        width: 44, height: 44, borderRadius: 10,
                        background: '#f3e5f5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <i className="fas fa-pallet" style={{ fontSize: '1.1rem', color: '#8e44ad' }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#2c3e50' }}>
                          Palete {palete.numero_palete}
                        </div>
                        {palete.cliente_razao_social && (
                          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                            <i className="fas fa-building" style={{ marginRight: 4 }}></i>
                            {palete.cliente_razao_social}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                          <span style={{
                            fontSize: '0.78rem', padding: '2px 8px',
                            background: '#e8f5e9', borderRadius: 4, color: '#2e7d32'
                          }}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: 4 }}></i>
                            {palete.area_codigo || palete.area_nome}{palete.area_codigo && palete.area_nome ? ` (${palete.area_nome})` : ''}
                          </span>
                          {palete.total_leituras > 0 && (
                            <span style={{
                              fontSize: '0.78rem', padding: '2px 8px',
                              background: '#e3f2fd', borderRadius: 4, color: '#1565c0'
                            }}>
                              <i className="fas fa-barcode" style={{ marginRight: 4 }}></i>
                              {palete.total_leituras} leitura{palete.total_leituras !== 1 ? 's' : ''}
                            </span>
                          )}
                          {palete.tipo_palete === 'agendamento' && (
                            <span style={{
                              fontSize: '0.78rem', padding: '2px 8px',
                              background: '#fff3e0', borderRadius: 4, color: '#e65100'
                            }}>
                              <i className="fas fa-calendar" style={{ marginRight: 4 }}></i>
                              Agendamento
                            </span>
                          )}
                          {palete.tem_ponto_corte && (
                            <span style={{
                              fontSize: '0.78rem', padding: '2px 8px',
                              background: '#fff3e0', borderRadius: 4, color: '#e65100'
                            }}>
                              <i className="fas fa-cut" style={{ marginRight: 4 }}></i>
                              Corte{palete.pedidos_com_corte && palete.pedidos_com_corte.length > 0 ? ` (${palete.pedidos_com_corte.length} pedido${palete.pedidos_com_corte.length !== 1 ? 's' : ''})` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <i className="fas fa-chevron-right" style={{ color: '#bdc3c7', fontSize: '0.9rem' }}></i>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Modal de seleção de palete quando múltiplos paletes para mesma área */}
        {selectPaleteAreaModal.show && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 16
          }} onClick={() => { setSelectPaleteAreaModal({ show: false, paletes: [], areaCodigo: '' }); setSelectPaleteAreaError(''); setTimeout(() => areaCodeRef.current?.focus(), 100); }}>
            <div style={{
              background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%',
              maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>
                  <i className="fas fa-pallet" style={{ marginRight: 8, color: '#8e44ad' }}></i>
                  Selecione o Palete
                </h3>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999' }}
                  onClick={() => { setSelectPaleteAreaModal({ show: false, paletes: [], areaCodigo: '' }); setSelectPaleteAreaError(''); setTimeout(() => areaCodeRef.current?.focus(), 100); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div style={{ padding: 20 }}>
                <p style={{ color: '#7f8c8d', marginBottom: 16, fontSize: 14 }}>
                  Foram encontrados <strong>{selectPaleteAreaModal.paletes.length} paletes</strong> para a área <strong>{selectPaleteAreaModal.areaCodigo}</strong>. Bipe o código do palete ou selecione manualmente:
                </p>
                <div className="precarreg-input-group" style={{ marginBottom: 16 }}>
                  <div className="precarreg-input-with-action">
                    <input
                      ref={paleteModalRef}
                      type="text"
                      inputMode={showPaleteModalKeyboard ? 'numeric' : 'none'}
                      className="precarreg-input mono"
                      placeholder="Bipe o número do palete..."
                      autoFocus
                      onChange={() => setSelectPaleteAreaError('')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const val = (e.target as HTMLInputElement).value.trim();
                          const numPalete = parseInt(val);
                          if (!numPalete) return;
                          const match = selectPaleteAreaModal.paletes.find(p => p.numero_palete === numPalete);
                          if (match) {
                            setSelectPaleteAreaModal({ show: false, paletes: [], areaCodigo: '' });
                            handleSelecionarPaleteLista(match);
                          } else {
                            setSelectPaleteAreaError(`Palete #${val} não pertence à área ${selectPaleteAreaModal.areaCodigo}`);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }
                      }}
                      style={selectPaleteAreaError ? { borderColor: '#e74c3c' } : undefined}
                    />
                    {configGlobal?.habilitar_camera_scanner ? (
                      <div className="precarreg-scan-options-wrapper" ref={paleteModalScanOptionsRef}>
                        <button
                          type="button"
                          className={`precarreg-scan-options-btn ${showPaleteModalScanOptions ? 'open' : ''}`}
                          onClick={() => setShowPaleteModalScanOptions(!showPaleteModalScanOptions)}
                          title="Opções de entrada"
                        >
                          <i className={`fas ${showPaleteModalScanOptions ? 'fa-times' : 'fa-plus'}`}></i>
                        </button>
                        {showPaleteModalScanOptions && (
                          <div className="precarreg-scan-options-menu">
                            <button
                              type="button"
                              onClick={() => {
                                setShowPaleteModalKeyboard(!showPaleteModalKeyboard);
                                setShowPaleteModalScanOptions(false);
                                setTimeout(() => {
                                  if (paleteModalRef.current) {
                                    paleteModalRef.current.blur();
                                    paleteModalRef.current.focus();
                                  }
                                }, 100);
                              }}
                            >
                              <i className={`fas fa-keyboard scan-option-icon-keyboard ${showPaleteModalKeyboard ? 'active' : ''}`}></i>
                              {showPaleteModalKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowPaleteModalScanOptions(false);
                                setShowPaleteModalCameraScanner(true);
                              }}
                            >
                              <i className="fas fa-camera scan-option-icon-camera"></i>
                              Escanear com câmera
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`precarreg-keyboard-toggle ${showPaleteModalKeyboard ? 'active' : ''}`}
                        onClick={() => {
                          setShowPaleteModalKeyboard(!showPaleteModalKeyboard);
                          setTimeout(() => {
                            if (paleteModalRef.current) {
                              paleteModalRef.current.blur();
                              paleteModalRef.current.focus();
                            }
                          }, 100);
                        }}
                        title={showPaleteModalKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                      >
                        <i className="fas fa-keyboard"></i>
                      </button>
                    )}
                  </div>
                  {selectPaleteAreaError && (
                    <div style={{
                      marginTop: 6, fontSize: '0.82rem', color: '#e74c3c',
                      display: 'flex', alignItems: 'center', gap: 6
                    }}>
                      <i className="fas fa-exclamation-circle"></i>
                      {selectPaleteAreaError}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selectPaleteAreaModal.paletes.map(palete => (
                    <button
                      key={palete.id}
                      type="button"
                      onClick={() => {
                        setSelectPaleteAreaModal({ show: false, paletes: [], areaCodigo: '' });
                        handleSelecionarPaleteLista(palete);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px', background: '#fff', border: '1px solid #e0e0e0',
                        borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8e44ad'; e.currentTarget.style.background = '#faf5ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = '#fff'; }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: 8,
                        background: '#f3e5f5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <i className="fas fa-pallet" style={{ fontSize: '1rem', color: '#8e44ad' }}></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#2c3e50' }}>
                          Palete {palete.numero_palete}
                        </div>
                        {palete.total_leituras > 0 && (
                          <span style={{
                            fontSize: '0.78rem', padding: '2px 8px',
                            background: '#e3f2fd', borderRadius: 4, color: '#1565c0'
                          }}>
                            <i className="fas fa-barcode" style={{ marginRight: 4 }}></i>
                            {palete.total_leituras} leitura{palete.total_leituras !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <i className="fas fa-chevron-right" style={{ color: '#bdc3c7', fontSize: '0.9rem' }}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de seleção de cliente para gerar palete */}
        {gerarPaleteClienteModal.show && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 16
          }} onClick={() => { setGerarPaleteClienteModal({ show: false, clientes: [] }); setError(''); }}>
            <div style={{
              background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%',
              maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}><i className="fas fa-building" style={{ marginRight: 8 }}></i> Selecione o Cliente</h3>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999' }}
                  onClick={() => { setGerarPaleteClienteModal({ show: false, clientes: [] }); setError(''); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div style={{ padding: 20 }}>
                {error && (
                  <div style={{ background: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{error}
                  </div>
                )}
                <p style={{ color: '#7f8c8d', marginBottom: 16, fontSize: 14 }}>
                  Você possui mais de um cliente vinculado. Selecione o cliente para gerar o novo palete:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {gerarPaleteClienteModal.clientes.map(cli => (
                    <button
                      key={cli.id}
                      type="button"
                      className="precarreg-btn secondary"
                      style={{ textAlign: 'left', padding: '12px 16px' }}
                      disabled={gerarPaleteLoading}
                      onClick={() => handleSelecionarClienteGerarPalete(cli.id)}
                    >
                      <div>
                        <strong>{cli.razao_social}</strong>
                        <br />
                        <span style={{ fontSize: 12, color: '#7f8c8d' }}>CNPJ: {formatCnpj(cli.cnpj)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de seleção de área de destino para gerar palete */}
        {gerarPaleteAreaModal.show && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 16
          }} onClick={() => { setGerarPaleteAreaModal({ show: false, areas: [], clienteId: 0 }); setError(''); }}>
            <div style={{
              background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%',
              maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}><i className="fas fa-map-marker-alt" style={{ marginRight: 8 }}></i> Área de Destino</h3>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999' }}
                  onClick={() => { setGerarPaleteAreaModal({ show: false, areas: [], clienteId: 0 }); setError(''); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div style={{ padding: 20 }}>
                {error && (
                  <div style={{ background: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{error}
                  </div>
                )}
                <p style={{ color: '#7f8c8d', marginBottom: 16, fontSize: 14 }}>
                  Selecione a área de destino para o novo palete:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {gerarPaleteAreaModal.areas.map(area => (
                    <button
                      key={area.id}
                      type="button"
                      className="precarreg-btn secondary"
                      style={{ textAlign: 'left', padding: '12px 16px' }}
                      disabled={gerarPaleteLoading}
                      onClick={() => handleSelecionarAreaGerarPalete(area.id)}
                    >
                      <div>
                        <strong>{area.nome}</strong>
                        {area.codigo && <span style={{ marginLeft: 8, fontSize: 12, color: '#7f8c8d' }}>({area.codigo})</span>}
                        {area.regiao && (
                          <>
                            <br />
                            <span style={{ fontSize: 12, color: '#7f8c8d' }}>{area.regiao}</span>
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de seleção de tipo de palete */}
        {gerarPaleteTipoModal.show && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 16
          }} onClick={() => { setGerarPaleteTipoModal({ show: false, clienteId: 0, areaEntregaId: 0 }); setShowAgendamentoInput(false); setDataAgendamento(''); setError(''); }}>
            <div style={{
              background: '#fff', borderRadius: 12, maxWidth: 420, width: '100%',
              maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}><i className="fas fa-pallet" style={{ marginRight: 8 }}></i> Tipo de Palete</h3>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999' }}
                  onClick={() => { setGerarPaleteTipoModal({ show: false, clienteId: 0, areaEntregaId: 0 }); setShowAgendamentoInput(false); setDataAgendamento(''); setError(''); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div style={{ padding: 20 }}>
                {error && (
                  <div style={{ background: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{error}
                  </div>
                )}
                <p style={{ color: '#7f8c8d', marginBottom: 16, fontSize: 14 }}>
                  Selecione o tipo do palete:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    type="button"
                    className="precarreg-btn secondary"
                    style={{ textAlign: 'left', padding: '14px 16px' }}
                    disabled={gerarPaleteLoading}
                    onClick={() => handleSelecionarTipoPalete('normal')}
                  >
                    <i className="fas fa-box" style={{ color: '#2980b9', marginRight: 8 }}></i>
                    <div>
                      <strong>Palete Normal</strong>
                      <br />
                      <span style={{ fontSize: 12, color: '#7f8c8d' }}>Palete de carga regular</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="precarreg-btn secondary"
                    style={{ textAlign: 'left', padding: '14px 16px', background: showAgendamentoInput ? '#f3e8ff' : undefined, borderColor: showAgendamentoInput ? '#8e44ad' : undefined }}
                    disabled={gerarPaleteLoading}
                    onClick={() => {
                      setShowAgendamentoInput(true);
                      setTimeout(() => {
                        const el = document.getElementById('agendamento-data-input');
                        if (el) el.focus();
                      }, 100);
                    }}
                  >
                    <i className="fas fa-calendar-alt" style={{ color: '#8e44ad', marginRight: 8 }}></i>
                    <div>
                      <strong>Palete Agendado</strong>
                      <br />
                      <span style={{ fontSize: 12, color: '#7f8c8d' }}>Palete com data e hora de agendamento</span>
                    </div>
                  </button>
                  {showAgendamentoInput && (
                    <div style={{ marginTop: 4, padding: '12px', background: '#f9f5ff', borderRadius: 8, border: '1px solid #e0d4f5' }}>
                      <label style={{ fontSize: 13, color: '#555', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                        <i className="fas fa-clock" style={{ marginRight: 6, color: '#8e44ad' }}></i>
                        Data e hora de agendamento:
                      </label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          id="agendamento-data-input"
                          type="datetime-local"
                          className="precarreg-input"
                          style={{ flex: 1, padding: '10px 12px', fontSize: '0.9rem' }}
                          value={dataAgendamento}
                          onChange={(e) => setDataAgendamento(e.target.value)}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button
                          type="button"
                          className="precarreg-btn primary"
                          style={{ flex: 1, padding: '10px 16px', fontSize: '0.9rem' }}
                          disabled={gerarPaleteLoading || !dataAgendamento}
                          onClick={() => handleSelecionarTipoPalete('agendamento', dataAgendamento)}
                        >
                          {gerarPaleteLoading
                            ? <i className="fas fa-spinner fa-spin"></i>
                            : <><i className="fas fa-check"></i> Confirmar Agendado</>
                          }
                        </button>
                        <button
                          type="button"
                          className="precarreg-btn secondary"
                          style={{ padding: '10px 12px', fontSize: '0.9rem', width: 'auto' }}
                          onClick={() => { setShowAgendamentoInput(false); setDataAgendamento(''); }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================
            Step 2: Bipagem Contínua
            ============================ */}
        {etapa === 'scanning' && cliente && paleteInfo && (
          <>
            <div className="precarreg-card">
              {/* Client info */}
              <div className="precarreg-cliente-info">
                <span className="cliente-nome">{cliente.razao_social}</span>
                <span className="cliente-cnpj">CNPJ: {formatCnpj(cliente.cnpj)}</span>
              </div>

              {/* Palete & Area info */}
              <div className="precarreg-info-badges">
                <div className="precarreg-badge blue">
                  <i className="fas fa-pallet"></i>
                  <strong>Palete #{paleteInfo.numero_palete}</strong>
                </div>
                <div className="precarreg-badge green">
                  <i className="fas fa-map-marker-alt"></i>
                  {paleteInfo.area_codigo || paleteInfo.area_nome}
                  {paleteInfo.area_codigo && paleteInfo.area_nome && <span className="badge-sub"> ({paleteInfo.area_nome})</span>}
                </div>
                {paleteInfo.doca && (
                  <div className="precarreg-badge purple">
                    <i className="fas fa-warehouse"></i>
                    Doca: {paleteInfo.doca}
                  </div>
                )}
                {paleteInfo.tipo_palete === 'agendamento' && (
                  <div
                    className="precarreg-badge"
                    style={{ background: '#fde8d0', color: '#a04000', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <i className="fas fa-calendar-alt"></i>
                      Agendamento
                    </div>
                    {paleteInfo.data_agendamento && (
                      <div style={{ fontSize: 12, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i className="fas fa-clock" style={{ fontSize: 11 }}></i>
                        {new Date(paleteInfo.data_agendamento).toLocaleDateString('pt-BR')} às {new Date(paleteInfo.data_agendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Summary counters */}
              {contadores.total > 0 && (
                <div className="precarreg-counters">
                  <div className="precarreg-counter-chip success">
                    <i className="fas fa-check-circle"></i>
                    {contadores.sucesso} Sucesso
                  </div>
                  {contadores.duplicado > 0 && (
                    <div className="precarreg-counter-chip warning">
                      <i className="fas fa-clone"></i>
                      {contadores.duplicado} Duplicado{contadores.duplicado > 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.divergencia > 0 && (
                    <div className="precarreg-counter-chip danger">
                      <i className="fas fa-exclamation-triangle"></i>
                      {contadores.divergencia} Divergência{contadores.divergencia > 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.erro > 0 && (
                    <div className="precarreg-counter-chip purple-chip">
                      <i className="fas fa-times-circle"></i>
                      {contadores.erro} Erro{contadores.erro > 1 ? 's' : ''}
                    </div>
                  )}
                  {contadores.removida > 0 && (
                    <div className="precarreg-counter-chip" style={{ background: '#f3f4f6', color: '#6b7280' }}>
                      <i className="fas fa-trash-alt"></i>
                      {contadores.removida} Removida{contadores.removida > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}

              {/* Totais logísticos (peso, cubagem, volumes) */}
              {contadores.sucesso > 0 && (totaisLogisticos.peso_total > 0 || totaisLogisticos.cubagem_total > 0 || totaisLogisticos.volumes_total > 0) && (
                <div style={{
                  display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8,
                  padding: '10px 12px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #d0e3ff'
                }}>
                  {totaisLogisticos.peso_total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#1565c0' }}>
                      <i className="fas fa-weight-hanging" style={{ width: 16 }}></i>
                      <strong>{totaisLogisticos.peso_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg</strong>
                    </div>
                  )}
                  {totaisLogisticos.cubagem_total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#6a1b9a' }}>
                      <i className="fas fa-cube" style={{ width: 16 }}></i>
                      <strong>{totaisLogisticos.cubagem_total.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³</strong>
                    </div>
                  )}
                  {totaisLogisticos.volumes_total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: '#2e7d32' }}>
                      <i className="fas fa-boxes-stacked" style={{ width: 16 }}></i>
                      <strong>{totaisLogisticos.volumes_total} vol.</strong>
                    </div>
                  )}
                </div>
              )}

              {/* Last scan status feedback */}
              {ultimoStatus && (
                <div
                  className="precarreg-scan-feedback"
                  style={{
                    background: STATUS_CONFIG[ultimoStatus.status].bg,
                    color: STATUS_CONFIG[ultimoStatus.status].color,
                    borderLeftColor: STATUS_CONFIG[ultimoStatus.status].color,
                  }}
                >
                  <i className={STATUS_CONFIG[ultimoStatus.status].icon}></i>
                  {ultimoStatus.mensagem}
                </div>
              )}

              {/* Scan input */}
              <form onSubmit={handleScanEtiqueta}>
                <div className="precarreg-input-group">
                  <label>Bipe a próxima etiqueta</label>
                  <div className="precarreg-input-with-action">
                    <input
                      ref={etiquetaRef}
                      type="text"
                      inputMode={showKeyboard ? 'text' : 'none'}
                      className="precarreg-input mono"
                      placeholder="Bipe ou digite o QR Code / Etiqueta"
                      autoFocus
                      disabled={scanLoading}
                    />
                    {configGlobal?.habilitar_camera_scanner ? (
                      <div className="precarreg-scan-options-wrapper" ref={scanOptionsRef}>
                        <button
                          type="button"
                          className={`precarreg-scan-options-btn ${showScanOptions ? 'open' : ''}`}
                          onClick={() => setShowScanOptions(!showScanOptions)}
                          title="Opções de entrada"
                        >
                          <i className={`fas ${showScanOptions ? 'fa-times' : 'fa-plus'}`}></i>
                        </button>
                        {showScanOptions && (
                          <div className="precarreg-scan-options-menu">
                            <button
                              type="button"
                              onClick={() => {
                                setShowKeyboard(!showKeyboard);
                                setShowScanOptions(false);
                                setTimeout(() => {
                                  if (etiquetaRef.current) {
                                    etiquetaRef.current.blur();
                                    etiquetaRef.current.focus();
                                  }
                                }, 100);
                              }}
                            >
                              <i className={`fas fa-keyboard scan-option-icon-keyboard ${showKeyboard ? 'active' : ''}`}></i>
                              {showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowScanOptions(false);
                                setShowCameraScanner(true);
                              }}
                              disabled={scanLoading}
                            >
                              <i className="fas fa-camera scan-option-icon-camera"></i>
                              Escanear com câmera
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`precarreg-keyboard-toggle ${showKeyboard ? 'active' : ''}`}
                        onClick={() => {
                          setShowKeyboard(!showKeyboard);
                          setTimeout(() => {
                            if (etiquetaRef.current) {
                              etiquetaRef.current.blur();
                              etiquetaRef.current.focus();
                            }
                          }, 100);
                        }}
                        title={showKeyboard ? 'Ocultar teclado' : 'Mostrar teclado'}
                      >
                        <i className="fas fa-keyboard"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="precarreg-btn-group">
                  <button type="button" className="precarreg-btn secondary" onClick={handleReset}>
                    <i className="fas fa-redo"></i> {isPWA ? 'Voltar' : 'Novo Palete'}
                  </button>
                  <button type="submit" className="precarreg-btn primary" disabled={scanLoading}>
                    {scanLoading ? <><i className="fas fa-spinner fa-spin"></i> Registrando...</> : <><i className="fas fa-barcode"></i> Registrar</>}
                  </button>
                </div>
              </form>

              {/* Button to analyze divergences - only shown when there are actual divergences */}
              {contadores.sucesso > 0 && temDivergenciaReal && (
                <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
                  <button
                    type="button"
                    className="precarreg-btn primary"
                    style={{ width: '100%', background: '#e74c3c' }}
                    onClick={handleAnalisarDivergencias}
                    disabled={divergenciaLoading}
                  >
                    {divergenciaLoading
                      ? <><i className="fas fa-spinner fa-spin"></i> Analisando...</>
                      : <><i className="fas fa-search-minus"></i> Analisar Divergências</>
                    }
                  </button>
                </div>
              )}
            </div>

            {/* ============================
                Histórico de Bipagem
                ============================ */}
            {historico.length > 0 && (
              <div className="precarreg-card" style={{ marginTop: 16 }}>
                <div className="precarreg-card-header" style={{ cursor: 'pointer' }} onClick={() => setHistoricoVisivel(v => !v)}>
                  <i className="fas fa-history"></i>
                  <h3>Histórico de Bipagem ({historico.length})</h3>
                  <button
                    type="button"
                    className="precarreg-toggle-historico-btn"
                    title={historicoVisivel ? 'Ocultar histórico' : 'Mostrar histórico'}
                  >
                    <i className={historicoVisivel ? 'fas fa-chevron-up' : 'fas fa-chevron-down'}></i>
                    {historicoVisivel ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                {historicoVisivel && <div className="precarreg-data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Etiqueta</th>
                        <th>Status</th>
                        <th>Hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historico.map((item) => {
                        const badge = STATUS_CONFIG[item.status];
                        const podeRemover = (item.status === 'sucesso' || item.status === 'divergencia') && item.leituraId;
                        return (
                          <tr key={item.id} style={item.status === 'removida' ? { opacity: 0.5 } : undefined}>
                            <td className="precarreg-hist-etiqueta">
                              <span className="hist-codigo" style={item.status === 'removida' ? { textDecoration: 'line-through' } : undefined}>{item.codigo}</span>
                              {item.mensagem && (
                                <span className="hist-detalhe">{item.mensagem}</span>
                              )}
                              {podeRemover && (
                                <button
                                  type="button"
                                  className="precarreg-remover-btn"
                                  onClick={(e) => { e.stopPropagation(); handleAbrirRemocao(item); }}
                                  title="Remover etiqueta deste palete"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              )}
                            </td>
                            <td>
                              <span
                                className="precarreg-status-badge"
                                style={{ background: badge.bg, color: badge.color }}
                              >
                                <i className={badge.icon}></i>
                                {badge.label}
                              </span>
                            </td>
                            <td className="precarreg-hist-hora">
                              {item.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>}
              </div>
            )}

            {/* ============================
                Etiquetas por Pedido (abaixo do histórico)
                ============================ */}
            {Object.keys(pedidosVolumes).length > 0 && (
              <div className="precarreg-card" style={{ marginTop: 16 }}>
                <div className="precarreg-card-header" style={{ background: '#fffde7' }}>
                  <i className="fas fa-tags" style={{ color: '#f57f17' }}></i>
                  <h3 style={{ color: '#f57f17' }}>Etiquetas por Pedido</h3>
                </div>
                <div style={{ padding: '12px 14px' }}>
                  {Object.entries(pedidosVolumes).map(([pedido, info]) => {
                    const faltantes: number[] = [];
                    for (let v = 1; v <= info.total_volumes; v++) {
                      if (!info.volumes_bipados.has(v)) faltantes.push(v);
                    }
                    // Pegar o código bipado mais longo como referência para gerar os irmãos,
                    // pois códigos com zeros à esquerda (ex: "0002") são mais longos que sem (ex: "1")
                    const codigos = Array.from(info.codigos_bipados.values());
                    const codigoRef = codigos.length > 0
                      ? codigos.reduce((longest, current) => current.length > longest.length ? current : longest)
                      : null;

                    return (
                      <div key={pedido} style={{
                        marginBottom: 10, padding: '10px 12px', background: '#fff', borderRadius: 8,
                        border: faltantes.length > 0 ? '1px solid #ffe082' : '1px solid #c8e6c9'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>
                            <i className="fas fa-file-invoice" style={{ marginRight: 4, color: '#1565c0' }}></i>
                            Pedido {pedido}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                              fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                              background: faltantes.length === 0 ? '#e8f5e9' : '#fff3e0',
                              color: faltantes.length === 0 ? '#2e7d32' : '#e65100'
                            }}>
                              {info.volumes_bipados.size}/{info.total_volumes}
                            </span>
                            {info.volumes_bipados.size > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRemocaoPedidoModal({
                                    show: true,
                                    numeroPedido: pedido,
                                    totalVolumes: info.total_volumes,
                                    volumesBipados: info.volumes_bipados.size,
                                  });
                                }}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
                                  borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                                title={`Remover todas as etiquetas do pedido ${pedido}`}
                              >
                                <i className="fas fa-trash-alt" style={{ fontSize: '0.85rem', color: '#e74c3c' }}></i>
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {Array.from({ length: info.total_volumes }, (_, i) => i + 1).map(v => {
                            const bipado = info.volumes_bipados.has(v);
                            // Usar código real se bipado, ou gerar código irmão se pendente
                            const codigoLabel = info.codigos_bipados.get(v)
                              || (codigoRef ? gerarCodigoIrmao(codigoRef, v) : null)
                              || `Vol. ${v}/${info.total_volumes}`;
                            return (
                              <div key={v} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 10px', borderRadius: 6, fontSize: '0.8rem',
                                background: bipado ? '#e8f5e9' : '#fff8e1',
                                border: `1px solid ${bipado ? '#a5d6a7' : '#ffe082'}`,
                              }}>
                                <i
                                  className={bipado ? 'fas fa-check-circle' : 'fas fa-clock'}
                                  style={{ fontSize: '0.85rem', color: bipado ? '#2e7d32' : '#f57f17', flexShrink: 0 }}
                                ></i>
                                <span style={{
                                  fontFamily: 'monospace', fontWeight: 500, color: bipado ? '#2e7d32' : '#333',
                                  wordBreak: 'break-all', flex: 1
                                }}>
                                  {codigoLabel}
                                </span>
                                <span style={{
                                  fontSize: '0.7rem', fontWeight: 600, padding: '1px 6px', borderRadius: 8,
                                  background: bipado ? '#c8e6c9' : '#ffe082',
                                  color: bipado ? '#1b5e20' : '#e65100',
                                  whiteSpace: 'nowrap', flexShrink: 0
                                }}>
                                  {bipado ? 'Conferida' : 'Pendente'}
                                </span>
                                {bipado && (() => {
                                  const codigoBipado = info.codigos_bipados.get(v);
                                  const historicoItem = codigoBipado
                                    ? historico.find(h => h.codigo === codigoBipado && h.status !== 'removida' && h.leituraId && h.conferenciaIdOrigem)
                                    : null;
                                  return historicoItem ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAbrirRemocao(historicoItem);
                                      }}
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: '2px 4px', flexShrink: 0, display: 'flex', alignItems: 'center',
                                      }}
                                      title={`Remover etiqueta vol. ${v}`}
                                    >
                                      <i className="fas fa-times-circle" style={{ fontSize: '0.8rem', color: '#e74c3c' }}></i>
                                    </button>
                                  ) : null;
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ============================
            Step 3: Tratativa de Divergências
            ============================ */}
        {etapa === 'divergencias' && (
          <>
            {/* Success message */}
            {divergenciaSuccess ? (
              <div className="precarreg-card">
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <i className="fas fa-check-circle" style={{ fontSize: 48, color: '#27ae60', marginBottom: 16 }}></i>
                  <h3 style={{ color: '#27ae60', marginBottom: 8 }}>
                    {totalLeiturasRemovidas === 0 && pedidosDivergentesRemovidos.length > 0
                      ? 'Ponto de corte registrado com sucesso!'
                      : 'Ocorrências registradas com sucesso!'}
                  </h3>
                  <p style={{ color: '#7f8c8d', marginBottom: 12 }}>
                    {totalLeiturasRemovidas === 0 && pedidosDivergentesRemovidos.length > 0
                      ? 'O pallet foi finalizado com as etiquetas já bipadas. As etiquetas restantes serão geradas em novos pallets.'
                      : 'As divergências foram registradas e podem ser visualizadas na página de Não Conformidades.'}
                  </p>
                </div>

                {/* Instrução para ponto de corte */}
                {totalLeiturasRemovidas === 0 && pedidosDivergentesRemovidos.length > 0 && (
                  <div style={{
                    margin: '0 20px 20px', padding: 20, background: '#e8f5e9', borderRadius: 12,
                    border: '2px solid #a5d6a7'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', background: '#43a047',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        <i className="fas fa-cut" style={{ fontSize: '1.3rem', color: 'white' }}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px', color: '#2e7d32', fontSize: '1rem' }}>
                          <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
                          Ponto de corte aplicado
                        </h4>
                        <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#1b5e20', lineHeight: 1.5 }}>
                          As etiquetas <strong>já bipadas permanecem neste pallet</strong>.
                          Ao iniciar um novo pallet para o mesmo pedido, o sistema gerará <strong>apenas as etiquetas restantes</strong>.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {pedidosDivergentesRemovidos.map(pedido => (
                            <div key={pedido} style={{
                              padding: '8px 14px', background: '#f1f8e9', borderRadius: 8,
                              border: '1px solid #c5e1a5', display: 'flex', alignItems: 'center', gap: 8,
                              fontSize: '0.9rem'
                            }}>
                              <i className="fas fa-cut" style={{ color: '#558b2f' }}></i>
                              <strong>Pedido {pedido}</strong>
                              <span style={{ color: '#689f38', fontSize: '0.8rem' }}>- ponto de corte registrado</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instrução de remoção das etiquetas físicas (divergência normal) */}
                {totalLeiturasRemovidas > 0 && pedidosDivergentesRemovidos.length > 0 && (
                  <div style={{
                    margin: '0 20px 20px', padding: 20, background: '#fff3e0', borderRadius: 12,
                    border: '2px solid #ffb74d'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', background: '#ff9800',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        <i className="fas fa-hand-paper" style={{ fontSize: '1.3rem', color: 'white' }}></i>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px', color: '#e65100', fontSize: '1rem' }}>
                          <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>
                          Ação necessária: Remova as etiquetas físicas!
                        </h4>
                        <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#bf360c', lineHeight: 1.5 }}>
                          Remova <strong>todas as etiquetas já bipadas</strong> dos pedidos divergentes abaixo.
                          Esses volumes devem aguardar o cliente resolver a divergência antes de embarcar em um novo carregamento.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {pedidosDivergentesRemovidos.map(pedido => (
                            <div key={pedido} style={{
                              padding: '8px 14px', background: '#fff8e1', borderRadius: 8,
                              border: '1px solid #ffe082', display: 'flex', alignItems: 'center', gap: 8,
                              fontSize: '0.9rem'
                            }}>
                              <i className="fas fa-tag" style={{ color: '#f57c00' }}></i>
                              <strong>Pedido {pedido}</strong>
                              <span style={{ color: '#8d6e63', fontSize: '0.8rem' }}>- remover etiquetas do palete</span>
                            </div>
                          ))}
                        </div>
                        <p style={{ margin: '12px 0 0', fontSize: '0.85rem', color: '#6d4c41' }}>
                          <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
                          {totalLeiturasRemovidas} leitura{totalLeiturasRemovidas !== 1 ? 's' : ''} removida{totalLeiturasRemovidas !== 1 ? 's' : ''} do sistema.
                          O palete agora está <strong style={{ color: '#2e7d32' }}>disponível para carregamento</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="precarreg-btn-group" style={{ justifyContent: 'center', padding: '0 20px 20px' }}>
                  <button type="button" className="precarreg-btn secondary" onClick={handleReset}>
                    <i className="fas fa-redo"></i> Nova Conferência
                  </button>
                  {!isPWA && (
                    <button type="button" className="precarreg-btn primary" onClick={() => navigate('/nao-conformidades')}>
                      <i className="fas fa-exclamation-triangle"></i> Ver Não Conformidades
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="precarreg-card">
                  <div className="precarreg-card-header">
                    <i className="fas fa-chart-bar"></i>
                    <h3>Resumo da Análise</h3>
                  </div>

                  <div className="precarreg-counters" style={{ marginTop: 12 }}>
                    <div className="precarreg-counter-chip success">
                      <i className="fas fa-check-circle"></i>
                      {pedidosCompletos.length} Pedido{pedidosCompletos.length !== 1 ? 's' : ''} completo{pedidosCompletos.length !== 1 ? 's' : ''}
                    </div>
                    {pedidosIncompletos.length > 0 && (
                      <div className="precarreg-counter-chip danger">
                        <i className="fas fa-exclamation-triangle"></i>
                        {pedidosIncompletos.length} Pedido{pedidosIncompletos.length !== 1 ? 's' : ''} com divergência{pedidosIncompletos.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {pedidosIncompletos.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                      <i className="fas fa-check-circle" style={{ fontSize: 40, color: '#27ae60', marginBottom: 12 }}></i>
                      <h3 style={{ color: '#27ae60' }}>Nenhuma divergência encontrada!</h3>
                      <p style={{ color: '#7f8c8d' }}>Todos os pedidos estão completos.</p>
                      <button type="button" className="precarreg-btn secondary" onClick={handleReset} style={{ marginTop: 16 }}>
                        <i className="fas fa-redo"></i> Nova Conferência
                      </button>
                    </div>
                  )}
                </div>

                {/* Divergent orders */}
                {pedidosIncompletos.length > 0 && (
                  <>
                    {/* Mode selector */}
                    <div className="precarreg-card" style={{ marginTop: 16 }}>
                      <div className="precarreg-card-header">
                        <i className="fas fa-cog"></i>
                        <h3>Modo de Registro</h3>
                      </div>
                      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        <button
                          type="button"
                          className={`precarreg-btn ${!modoOcorrenciaUnica ? 'primary' : 'secondary'}`}
                          onClick={() => setModoOcorrenciaUnica(false)}
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-list"></i> Individual por pedido
                        </button>
                        <button
                          type="button"
                          className={`precarreg-btn ${modoOcorrenciaUnica ? 'primary' : 'secondary'}`}
                          onClick={() => { setModoOcorrenciaUnica(true); toggleAllDivergencias(true); }}
                          style={{ flex: 1 }}
                        >
                          <i className="fas fa-layer-group"></i> Ocorrência única
                        </button>
                      </div>

                      {/* Unified occurrence form */}
                      {modoOcorrenciaUnica && (
                        <div style={{ marginTop: 16, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                          <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                            <label>Tipo de Ocorrência</label>
                            <select
                              className="precarreg-input"
                              value={ocorrenciaUnicaTipo}
                              onChange={e => setOcorrenciaUnicaTipo(e.target.value)}
                            >
                              {tiposOcorrencia.map(t => (
                                <option key={t.value} value={t.value}>{t.label}{t.ocorrencia_de_corte ? ' (Corte)' : ''}</option>
                              ))}
                            </select>
                            {tiposOcorrencia.find(t => t.value === ocorrenciaUnicaTipo)?.ocorrencia_de_corte && (
                              <small style={{ display: 'block', marginTop: 6, color: '#e65100', fontSize: '0.8rem' }}>
                                <i className="fas fa-cut" style={{ marginRight: 4 }}></i>
                                Ponto de corte: as etiquetas bipadas permanecerão neste pallet. As restantes serão geradas em novos pallets.
                              </small>
                            )}
                          </div>
                          <div className="precarreg-input-group">
                            <label>Justificativa</label>
                            <textarea
                              className="precarreg-input"
                              value={ocorrenciaUnicaJustificativa}
                              onChange={e => setOcorrenciaUnicaJustificativa(e.target.value)}
                              placeholder="Descreva a justificativa para todas as divergências selecionadas..."
                              rows={3}
                              style={{ resize: 'vertical', fontFamily: 'inherit' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pedidos divergentes list */}
                    <div className="precarreg-card" style={{ marginTop: 16 }}>
                      <div className="precarreg-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i className="fas fa-exclamation-triangle" style={{ color: '#e74c3c' }}></i>
                          <h3>Pedidos com Divergência ({pedidosIncompletos.length})</h3>
                        </div>
                        {!modoOcorrenciaUnica && (
                          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={Object.values(divergenciaForms).every(f => f.selected)}
                              onChange={e => toggleAllDivergencias(e.target.checked)}
                            />
                            Selecionar todos
                          </label>
                        )}
                      </div>

                      <div style={{ marginTop: 12 }}>
                        {pedidosIncompletos.map(pedido => {
                          const form = divergenciaForms[pedido.numero_pedido];
                          if (!form) return null;
                          const isExpanded = expandedPedido === pedido.numero_pedido;

                          return (
                            <div
                              key={pedido.numero_pedido}
                              style={{
                                border: form.selected ? '2px solid #e74c3c' : '1px solid #e0e0e0',
                                borderRadius: 8,
                                marginBottom: 12,
                                overflow: 'hidden',
                                background: form.selected ? '#fff5f5' : '#fff',
                              }}
                            >
                              {/* Order header */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  background: form.selected ? '#ffebee' : '#f8f9fa',
                                }}
                                onClick={() => setExpandedPedido(isExpanded ? null : pedido.numero_pedido)}
                              >
                                {!modoOcorrenciaUnica && (
                                  <input
                                    type="checkbox"
                                    checked={form.selected}
                                    onChange={e => {
                                      e.stopPropagation();
                                      updateDivergenciaForm(pedido.numero_pedido, 'selected', e.target.checked);
                                    }}
                                    onClick={e => e.stopPropagation()}
                                  />
                                )}
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <strong style={{ fontSize: '1rem' }}>Pedido {pedido.numero_pedido}</strong>
                                    <span
                                      className="precarreg-status-badge"
                                      style={{ background: '#ffebee', color: '#e74c3c', fontSize: '0.75rem' }}
                                    >
                                      <i className="fas fa-exclamation-triangle"></i>
                                      {pedido.quantidade_faltante} volume{pedido.quantidade_faltante !== 1 ? 's' : ''} faltante{pedido.quantidade_faltante !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: 4 }}>
                                    Conferidos: {pedido.quantidade_conferida} de {pedido.total_volumes} |
                                    Volumes bipados: [{pedido.volumes_conferidos.join(', ')}] |
                                    Faltantes: [{pedido.volumes_faltantes.join(', ')}]
                                  </div>
                                </div>
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#7f8c8d' }}></i>
                              </div>

                              {/* Expanded details */}
                              {isExpanded && (
                                <div style={{ padding: 16 }}>
                                  {/* Labels scanned for this order - oculto quando tipo é corte */}
                                  {(() => {
                                    const tipoAtivo = modoOcorrenciaUnica ? ocorrenciaUnicaTipo : form.ocorrencia_tipo;
                                    const isCorteAtivo = tiposOcorrencia.find(t => t.value === tipoAtivo)?.ocorrencia_de_corte || false;

                                    if (isCorteAtivo) {
                                      return (
                                        <div style={{
                                          marginBottom: 16,
                                          padding: 12,
                                          background: '#e8f5e9',
                                          borderRadius: 8,
                                          border: '1px solid #c8e6c9',
                                        }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                            <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
                                            <strong style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
                                              {pedido.leituras.length} etiqueta{pedido.leituras.length !== 1 ? 's' : ''} bipada{pedido.leituras.length !== 1 ? 's' : ''} permanecem neste pallet
                                            </strong>
                                          </div>
                                          <small style={{ color: '#558b2f', fontSize: '0.8rem' }}>
                                            No ponto de corte, as etiquetas já conferidas não recebem ocorrência.
                                            Apenas os volumes faltantes abaixo serão registrados.
                                          </small>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                          <h4 style={{ fontSize: '0.9rem', color: '#2c3e50', margin: 0 }}>
                                            <i className="fas fa-barcode" style={{ marginRight: 6 }}></i>
                                            Etiquetas bipadas ({pedido.leituras.length})
                                          </h4>
                                          {form.selected && (
                                            <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: '#7f8c8d' }}>
                                              <input
                                                type="checkbox"
                                                checked={form.selectedEtiquetas.size === pedido.leituras.length}
                                                onChange={e => toggleAllEtiquetas(pedido.numero_pedido, e.target.checked, pedido.leituras.length)}
                                              />
                                              Selecionar todas
                                            </label>
                                          )}
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                          {pedido.leituras.map((l, idx) => {
                                            const isSelected = form.selectedEtiquetas.has(idx);
                                            return (
                                              <div
                                                key={idx}
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 8,
                                                  padding: '6px 10px',
                                                  background: isSelected ? '#e8f5e9' : '#f5f5f5',
                                                  borderRadius: 4,
                                                  fontSize: '0.85rem',
                                                  border: isSelected ? '1px solid #c8e6c9' : '1px solid #e0e0e0',
                                                  cursor: form.selected ? 'pointer' : 'default',
                                                  opacity: isSelected ? 1 : 0.6,
                                                }}
                                                onClick={() => form.selected && toggleEtiqueta(pedido.numero_pedido, idx)}
                                              >
                                                {form.selected && (
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleEtiqueta(pedido.numero_pedido, idx)}
                                                    onClick={e => e.stopPropagation()}
                                                  />
                                                )}
                                                <code style={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{l.codigo_lido}</code>
                                                {l.resultado && l.resultado !== 'ok' && (
                                                  <span style={{
                                                    padding: '1px 6px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
                                                    color: l.resultado === 'duplicado' ? '#d97706' : l.resultado === 'removido' ? '#6b7280' : '#dc2626',
                                                    background: l.resultado === 'duplicado' ? '#fffbeb' : l.resultado === 'removido' ? '#f3f4f6' : '#fef2f2',
                                                    whiteSpace: 'nowrap'
                                                  }}>
                                                    {l.resultado === 'duplicado' ? 'Duplicada' : l.resultado === 'removido' ? 'Removida' : 'Divergente'}
                                                  </span>
                                                )}
                                                <span style={{ color: '#27ae60', whiteSpace: 'nowrap', marginLeft: 8 }}>
                                                  Vol. {l.volume_atual}/{l.total_volumes}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Missing volumes - with checkboxes (or auto-selected for corte) */}
                                  {(() => {
                                    const tipoAtivoFalt = modoOcorrenciaUnica ? ocorrenciaUnicaTipo : form.ocorrencia_tipo;
                                    const isCorteAtivoFalt = tiposOcorrencia.find(t => t.value === tipoAtivoFalt)?.ocorrencia_de_corte || false;

                                    return (
                                      <div style={{ marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                          <h4 style={{ fontSize: '0.9rem', color: isCorteAtivoFalt ? '#e65100' : '#e74c3c', margin: 0 }}>
                                            <i className={`fas ${isCorteAtivoFalt ? 'fa-cut' : 'fa-times-circle'}`} style={{ marginRight: 6 }}></i>
                                            {isCorteAtivoFalt
                                              ? `Volumes para novo pallet (${pedido.volumes_faltantes.length})`
                                              : `Volumes faltantes (${pedido.volumes_faltantes.length})`
                                            }
                                          </h4>
                                          {!isCorteAtivoFalt && form.selected && (
                                            <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: '#7f8c8d' }}>
                                              <input
                                                type="checkbox"
                                                checked={form.selectedFaltantes.size === pedido.volumes_faltantes.length}
                                                onChange={e => toggleAllFaltantes(pedido.numero_pedido, e.target.checked, pedido.volumes_faltantes)}
                                              />
                                              Selecionar todos
                                            </label>
                                          )}
                                        </div>
                                        {isCorteAtivoFalt && (
                                          <small style={{ display: 'block', marginBottom: 8, color: '#e65100', fontSize: '0.8rem' }}>
                                            Todos os volumes faltantes serão automaticamente direcionados para o próximo pallet.
                                          </small>
                                        )}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                          {pedido.volumes_faltantes.map(vol => {
                                            const isSelected = isCorteAtivoFalt ? true : form.selectedFaltantes.has(vol);
                                            return (
                                              <span
                                                key={vol}
                                                onClick={() => !isCorteAtivoFalt && form.selected && toggleFaltante(pedido.numero_pedido, vol)}
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 6,
                                                  padding: '4px 12px',
                                                  background: isCorteAtivoFalt ? '#fff3e0' : (isSelected ? '#ffebee' : '#f5f5f5'),
                                                  color: isCorteAtivoFalt ? '#e65100' : (isSelected ? '#e74c3c' : '#999'),
                                                  borderRadius: 20,
                                                  fontSize: '0.85rem',
                                                  fontWeight: 600,
                                                  cursor: isCorteAtivoFalt ? 'default' : (form.selected ? 'pointer' : 'default'),
                                                  border: isCorteAtivoFalt ? '1px solid #ffe0b2' : (isSelected ? '1px solid #ffcdd2' : '1px solid #e0e0e0'),
                                                  opacity: 1,
                                                }}
                                              >
                                                {!isCorteAtivoFalt && form.selected && (
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleFaltante(pedido.numero_pedido, vol)}
                                                    onClick={e => e.stopPropagation()}
                                                    style={{ margin: 0 }}
                                                  />
                                                )}
                                                Volume {vol} de {pedido.total_volumes}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Individual occurrence form (only in individual mode) */}
                                  {!modoOcorrenciaUnica && form.selected && (
                                    <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
                                      <h4 style={{ fontSize: '0.9rem', color: '#2c3e50', marginBottom: 12 }}>
                                        <i className="fas fa-file-alt" style={{ marginRight: 6 }}></i>
                                        Ocorrência para este pedido
                                      </h4>
                                      <div className="precarreg-input-group" style={{ marginBottom: 12 }}>
                                        <label>Tipo de Ocorrência</label>
                                        <select
                                          className="precarreg-input"
                                          value={form.ocorrencia_tipo}
                                          onChange={e => updateDivergenciaForm(pedido.numero_pedido, 'ocorrencia_tipo', e.target.value)}
                                        >
                                          {tiposOcorrencia.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}{t.ocorrencia_de_corte ? ' (Corte)' : ''}</option>
                                          ))}
                                        </select>
                                        {tiposOcorrencia.find(t => t.value === form.ocorrencia_tipo)?.ocorrencia_de_corte && (
                                          <small style={{ display: 'block', marginTop: 6, color: '#e65100', fontSize: '0.8rem' }}>
                                            <i className="fas fa-cut" style={{ marginRight: 4 }}></i>
                                            Ponto de corte: as etiquetas bipadas permanecerão neste pallet.
                                          </small>
                                        )}
                                      </div>
                                      <div className="precarreg-input-group">
                                        <label>Justificativa</label>
                                        <textarea
                                          className="precarreg-input"
                                          value={form.justificativa}
                                          onChange={e => updateDivergenciaForm(pedido.numero_pedido, 'justificativa', e.target.value)}
                                          placeholder="Descreva a justificativa para esta divergência..."
                                          rows={3}
                                          style={{ resize: 'vertical', fontFamily: 'inherit' }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="precarreg-card" style={{ marginTop: 16 }}>
                      <div className="precarreg-btn-group">
                        <button
                          type="button"
                          className="precarreg-btn secondary"
                          onClick={() => setEtapa('scanning')}
                        >
                          <i className="fas fa-arrow-left"></i> Voltar à Bipagem
                        </button>
                        <button
                          type="button"
                          className="precarreg-btn primary"
                          style={{ background: '#e74c3c' }}
                          onClick={handleSalvarDivergencias}
                          disabled={divergenciaSaving || Object.values(divergenciaForms).every(f => !f.selected)}
                        >
                          {divergenciaSaving
                            ? <><i className="fas fa-spinner fa-spin"></i> Salvando...</>
                            : <><i className="fas fa-save"></i> Registrar Ocorrências ({Object.values(divergenciaForms).filter(f => f.selected).length})</>
                          }
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>

    {/* Modal de transferência entre paletes */}
    {transferenciaModal.show && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 16
      }}>
        <div style={{
          background: 'white', borderRadius: 16, padding: '24px 20px', width: '100%', maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#fff3e0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
            }}>
              <i className="fas fa-exchange-alt" style={{ fontSize: '1.5rem', color: '#f57c00' }}></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#2c3e50' }}>Etiqueta em Outro Palete</h3>
            <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
              Esta etiqueta já foi bipada no{' '}
              <strong style={{ color: '#e65100' }}>
                Palete #{transferenciaModal.numeroPaleteAnterior || '?'}
              </strong>
            </p>
          </div>

          {/* Código da etiqueta */}
          <div style={{
            padding: '10px 14px', background: '#f8f9fa', borderRadius: 8,
            fontFamily: 'monospace', fontSize: '0.85rem', color: '#2c3e50',
            wordBreak: 'break-all', marginBottom: 20, textAlign: 'center',
            border: '1px solid #e0e0e0'
          }}>
            {transferenciaModal.codigo}
          </div>

          {/* Aviso sobre transferência */}
          <div style={{
            padding: '10px 14px', background: '#fff3e0', borderRadius: 8,
            fontSize: '0.82rem', color: '#e65100', marginBottom: 16,
            border: '1px solid #ffcc80', lineHeight: 1.5
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
            Deseja transferir os itens do Palete #{transferenciaModal.numeroPaleteAnterior || '?'} para este palete?
            <strong> Todas as etiquetas do Palete #{transferenciaModal.numeroPaleteAnterior || '?'} serão removidas</strong> e
            você precisará bipá-las novamente neste palete.
          </div>

          {/* Botões */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={handleTransferir}
              disabled={transferenciaLoading}
              style={{
                padding: '14px 20px', borderRadius: 10, border: 'none',
                background: '#1565c0', color: 'white', fontWeight: 600,
                fontSize: '0.9rem', cursor: transferenciaLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}
            >
              {transferenciaLoading
                ? <><i className="fas fa-spinner fa-spin"></i> Transferindo...</>
                : <><i className="fas fa-exchange-alt"></i> Sim, transferir para este palete (#{paleteInfo?.numero_palete})</>
              }
            </button>
            <button
              onClick={handleManterNoPaleteAnterior}
              disabled={transferenciaLoading}
              style={{
                padding: '14px 20px', borderRadius: 10,
                border: '2px solid #e0e0e0', background: 'white',
                color: '#546e7a', fontWeight: 600, fontSize: '0.9rem',
                cursor: transferenciaLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}
            >
              <i className="fas fa-ban"></i> Não, manter no Palete #{transferenciaModal.numeroPaleteAnterior || '?'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Modal de etiqueta duplicada */}
    {duplicataModal.show && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 2000, padding: 20,
      }}>
        <div style={{
          background: '#fff', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#fff3e0',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
            }}>
              <i className="fas fa-clone" style={{ fontSize: 24, color: '#f39c12' }}></i>
            </div>
            <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.1rem' }}>Etiqueta Duplicada</h3>
          </div>

          {/* Resumo */}
          <div style={{
            background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
            padding: 12, marginBottom: 20, fontSize: '0.9rem', color: '#5d4037',
          }}>
            <p style={{ margin: 0, marginBottom: 8 }}>
              <strong>Etiqueta:</strong> <code style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{duplicataModal.codigo}</code>
            </p>
            {duplicataModal.volumeAtual && duplicataModal.totalVolumes && (
              <p style={{ margin: 0, marginBottom: 8 }}>
                <strong>Volume {duplicataModal.volumeAtual} de {duplicataModal.totalVolumes}</strong> j\u00e1 foi bipado anteriormente.
              </p>
            )}
            {duplicataModal.numeroPedido && (
              <p style={{ margin: 0, marginBottom: 8 }}>
                <strong>Pedido:</strong> {duplicataModal.numeroPedido}
              </p>
            )}
            <p style={{ margin: 0, fontWeight: 600 }}>
              Por favor, revise e selecione o motivo da duplicidade:
            </p>
          </div>

          {/* Opções de ocorrência */}
          {temAutomaticoDuplicata ? (
            <>
              {/* Modo automático: botões rápidos */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  type="button"
                  className="precarreg-btn secondary"
                  style={{
                    width: '100%', padding: '14px 16px', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.95rem',
                  }}
                  onClick={() => handleDuplicataResponseAuto('erro_conferente')}
                  disabled={duplicataLoading}
                >
                  <i className="fas fa-user-times" style={{ fontSize: 18, color: '#3498db', minWidth: 24 }}></i>
                  <div>
                    <strong>Erro do Conferente</strong>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 2 }}>
                      O mesmo item foi bipado duas vezes por engano
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="precarreg-btn secondary"
                  style={{
                    width: '100%', padding: '14px 16px', textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.95rem',
                    border: '2px solid #e74c3c', background: '#fff5f5',
                  }}
                  onClick={() => handleDuplicataResponseAuto('duplicata_cliente')}
                  disabled={duplicataLoading}
                >
                  <i className="fas fa-box" style={{ fontSize: 18, color: '#e74c3c', minWidth: 24 }}></i>
                  <div>
                    <strong style={{ color: '#c0392b' }}>Cliente Enviou Duplicado</strong>
                    <div style={{ fontSize: '0.8rem', color: '#7f8c8d', marginTop: 2 }}>
                      O cliente enviou o mesmo item duas vezes com a mesma etiqueta
                    </div>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Modo manual: lista de ocorrências + observação */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>
                    Tipo de Ocorrência *
                  </label>
                  <select
                    value={duplicataOcorrenciaManual}
                    onChange={e => setDuplicataOcorrenciaManual(e.target.value)}
                    disabled={duplicataLoading}
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                      borderRadius: 8, fontSize: '0.95rem', background: '#fff',
                      color: duplicataOcorrenciaManual ? '#2c3e50' : '#999',
                    }}
                  >
                    <option value="">Selecione o tipo de ocorrência...</option>
                    {todosOcorrenciaTipos.map(t => (
                      <option key={t.id} value={t.codigo}>{t.nome}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50' }}>
                    Observação
                  </label>
                  <textarea
                    value={duplicataObservacao}
                    onChange={e => setDuplicataObservacao(e.target.value)}
                    disabled={duplicataLoading}
                    placeholder="Descreva o motivo da duplicidade..."
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #ddd',
                      borderRadius: 8, fontSize: '0.95rem', resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                <button
                  type="button"
                  className="precarreg-btn primary"
                  style={{
                    width: '100%', padding: '12px 16px', fontSize: '0.95rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                  onClick={handleDuplicataResponseManual}
                  disabled={duplicataLoading || !duplicataOcorrenciaManual}
                >
                  <i className="fas fa-save" style={{ fontSize: 16 }}></i>
                  Registrar Ocorrência
                </button>
              </div>
            </>
          )}

          {duplicataLoading && (
            <div style={{ textAlign: 'center', marginTop: 12, color: '#7f8c8d' }}>
              <i className="fas fa-spinner fa-spin"></i> Registrando ocorrência...
            </div>
          )}
        </div>
      </div>
    )}

    {/* Modal de confirmação de remoção de etiqueta */}
    {remocaoModal.show && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 16
      }}>
        <div style={{
          background: 'white', borderRadius: 16, padding: '24px 20px', width: '100%', maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#ffebee',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
            }}>
              <i className="fas fa-trash-alt" style={{ fontSize: '1.5rem', color: '#e74c3c' }}></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#2c3e50' }}>Remover Etiqueta</h3>
            <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
              Tem certeza que deseja remover esta etiqueta deste palete?
            </p>
          </div>

          <div style={{
            padding: '10px 14px', background: '#f8f9fa', borderRadius: 8,
            fontFamily: 'monospace', fontSize: '0.85rem', color: '#2c3e50',
            wordBreak: 'break-all', marginBottom: 16, textAlign: 'center',
            border: '1px solid #e0e0e0'
          }}>
            {remocaoModal.codigo}
          </div>

          <div style={{
            padding: '12px 14px', background: '#fff3e0', borderRadius: 8,
            fontSize: '0.85rem', color: '#e65100', marginBottom: 20, lineHeight: 1.5,
            border: '1px solid #ffe0b2'
          }}>
            <i className="fas fa-info-circle" style={{ marginRight: 6 }}></i>
            A etiqueta será marcada como <strong>removida</strong> neste palete e ficará liberada para ser bipada em outro palete.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setRemocaoModal({ show: false, historicoItemId: 0, codigo: '', leituraId: 0, conferenciaIdOrigem: 0 })}
              disabled={remocaoLoading}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10,
                border: '2px solid #e0e0e0', background: 'white',
                color: '#546e7a', fontWeight: 600, fontSize: '0.9rem',
                cursor: remocaoLoading ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmarRemocao}
              disabled={remocaoLoading}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10, border: 'none',
                background: '#e74c3c', color: 'white', fontWeight: 600,
                fontSize: '0.9rem', cursor: remocaoLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}
            >
              {remocaoLoading
                ? <><i className="fas fa-spinner fa-spin"></i> Removendo...</>
                : <><i className="fas fa-trash-alt"></i> Remover</>
              }
            </button>
          </div>
        </div>
      </div>
    )}
    {/* Modal de confirmação de remoção de pedido inteiro */}
    {remocaoPedidoModal.show && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: 16
      }}>
        <div style={{
          background: 'white', borderRadius: 16, padding: '24px 20px', width: '100%', maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#ffebee',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
            }}>
              <i className="fas fa-trash-alt" style={{ fontSize: '1.5rem', color: '#e74c3c' }}></i>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#2c3e50' }}>Remover Pedido do Palete</h3>
            <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
              Tem certeza que deseja remover <strong>todas as etiquetas</strong> do pedido <strong>{remocaoPedidoModal.numeroPedido}</strong>?
            </p>
          </div>

          <div style={{
            padding: '12px 14px', background: '#f8f9fa', borderRadius: 8,
            fontSize: '0.9rem', color: '#2c3e50', marginBottom: 16, textAlign: 'center',
            border: '1px solid #e0e0e0'
          }}>
            <i className="fas fa-file-invoice" style={{ marginRight: 6, color: '#1565c0' }}></i>
            Pedido <strong>{remocaoPedidoModal.numeroPedido}</strong>
            <span style={{ margin: '0 8px', color: '#bbb' }}>|</span>
            {remocaoPedidoModal.volumesBipados} etiqueta{remocaoPedidoModal.volumesBipados !== 1 ? 's' : ''} bipada{remocaoPedidoModal.volumesBipados !== 1 ? 's' : ''}
          </div>

          <div style={{
            padding: '12px 14px', background: '#ffebee', borderRadius: 8,
            fontSize: '0.85rem', color: '#c62828', marginBottom: 20, lineHeight: 1.5,
            border: '1px solid #ef9a9a'
          }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>
            Todas as {remocaoPedidoModal.volumesBipados} etiqueta{remocaoPedidoModal.volumesBipados !== 1 ? 's' : ''} deste pedido ser{remocaoPedidoModal.volumesBipados !== 1 ? 'ão' : 'á'} <strong>removida{remocaoPedidoModal.volumesBipados !== 1 ? 's' : ''}</strong> deste palete e o pedido será excluído da lista.
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setRemocaoPedidoModal({ show: false, numeroPedido: '', totalVolumes: 0, volumesBipados: 0 })}
              disabled={remocaoPedidoLoading}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10,
                border: '2px solid #e0e0e0', background: 'white',
                color: '#546e7a', fontWeight: 600, fontSize: '0.9rem',
                cursor: remocaoPedidoLoading ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmarRemocaoPedido}
              disabled={remocaoPedidoLoading}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10, border: 'none',
                background: '#e74c3c', color: 'white', fontWeight: 600,
                fontSize: '0.9rem', cursor: remocaoPedidoLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}
            >
              {remocaoPedidoLoading
                ? <><i className="fas fa-spinner fa-spin"></i> Removendo...</>
                : <><i className="fas fa-trash-alt"></i> Remover Pedido</>
              }
            </button>
          </div>
        </div>
      </div>
    )}
    <CameraScannerModal
      show={showCameraScanner}
      onClose={() => setShowCameraScanner(false)}
      onScan={(code) => {
        setShowCameraScanner(false);
        if (etiquetaRef.current) etiquetaRef.current.value = code;
        // Auto-submit após leitura
        setTimeout(() => {
          if (etiquetaRef.current) {
            etiquetaRef.current.form?.requestSubmit();
          }
        }, 100);
      }}
    />
    <CameraScannerModal
      show={showAreaCameraScanner}
      onClose={() => setShowAreaCameraScanner(false)}
      onScan={(code) => {
        setShowAreaCameraScanner(false);
        handleBuscarPorAreaCode(code);
      }}
    />
    <CameraScannerModal
      show={showPaleteModalCameraScanner}
      onClose={() => setShowPaleteModalCameraScanner(false)}
      onScan={(code) => {
        setShowPaleteModalCameraScanner(false);
        const numPalete = parseInt(code.trim());
        if (!numPalete || !selectPaleteAreaModal.show) return;
        const match = selectPaleteAreaModal.paletes.find(p => p.numero_palete === numPalete);
        if (match) {
          setSelectPaleteAreaModal({ show: false, paletes: [], areaCodigo: '' });
          handleSelecionarPaleteLista(match);
        } else {
          setSelectPaleteAreaError(`Palete #${code.trim()} não pertence à área ${selectPaleteAreaModal.areaCodigo}`);
          if (paleteModalRef.current) paleteModalRef.current.value = '';
        }
      }}
    />
    </div>
  );
};

export default PreCarregamentoConferencia;
